/* ============================================================
   Lettoon — Build-a-Pack builder (vanilla JS, no deps)
   Port of the Claude Design prototypes (Build a Pack.dc.html +
   Build a Pack - Mobile.dc.html) onto the Shopify (Pipeline) theme.

   The catalog is fetched client-side from
   /collections/<handle>/products.json (Liquid caps collection.products
   at 50 and server-rendering ~180 tiles was slow). Multi-design
   products (Theme / Set / Sticker Sheet axes) get an "N designs"
   badge and a variant picker in the quick-look; paper-material
   options stay a secondary switch. Bundle variants ("Both Themes",
   "All 6 Washi Tapes", "Complete Cozy Set", 5/10-card packs) are
   selectable and count as their full unit count toward the tiers
   (Nick 2026-07-13). Units come from assets/lt-pack-units.json
   (generated by tools/gen-pack-units.js, which also writes the
   lettoon.pack_units metafields the Function reads at checkout),
   with the same title heuristic as the Function as fallback.

   The tray shows an ESTIMATED discounted total. The real discount is
   enforced at checkout by the "lt-pack-discount" Shopify Function,
   which reads the `_lt_pack` line-item properties added on Add-to-cart.
   Keep PACK_PROP / ROLE_PROP in sync with the Function.
   ============================================================ */
(function () {
  'use strict';

  var PACK_PROP = '_lt_pack';        // groups a pack; value = pack token
  var ROLE_PROP = '_lt_pack_role';   // 'item' | 'journal' ('journal' is also the role of the coloring-book reward since #192: the string is a contract with the discount function and with carts in flight, so it never changes)

  var root = document.querySelector('[data-lt-bap]');
  if (!root) return;

  // ---- config from data attributes --------------------------------
  var SYM = root.dataset.sym || '€';
  var CUR = root.dataset.cur || 'EUR';
  var T1 = parseInt(root.dataset.t1, 10) || 5;
  var T1P = parseInt(root.dataset.t1p, 10) || 10;
  var T2 = parseInt(root.dataset.t2, 10) || 10;
  // Tier-2 default is 15% (Nick, 2026-07-17). The value normally comes from the
  // section settings via data-t2p; this fallback only matters if that attribute
  // is ever missing. It used to read 20 — the old ladder — which is now dead.
  var T2P = parseInt(root.dataset.t2p, 10) || 15;
  var J_PCT = parseInt(root.dataset.journalPct, 10) || 10;
  var J_MIN = parseInt(root.dataset.journalMin, 10) || 5;
  // #50: the "(… value)" amount for the blank Tomoe River insert line. Bound in
  // Liquid to the insert product's live price when the merchant picks it, else
  // an editable text default ('€6.99'). Already a formatted money string here.
  var INSERT_VALUE = root.dataset.insertValue || '';
  var CART_ADD = root.dataset.cartAdd || '/cart/add.js';
  var CART_URL = root.dataset.cartUrl || '/cart';
  var ROOT_URL = root.dataset.rootUrl || '/';
  if (ROOT_URL.slice(-1) !== '/') ROOT_URL += '/';
  var COLLECTIONS = [
    ['Stickers', root.dataset.colStickers],
    ['Washi', root.dataset.colWashi],
    ['Cards', root.dataset.colCards]
  ].filter(function (c) { return c[1]; });

  // #34-followup: the quick-look TYPE line ("Sticker sheet · €2.79 each") needs a
  // human product-type name, not the internal collection LABEL ('Stickers' /
  // 'Washi' / 'Cards') we use for filtering. This map mirrors the design
  // prototype's typeMeta names (Build a Pack.dc.html). Falls back to the label.
  // 'Stickers'/'Washi'/'Cards' here are the builder's OWN 3-way filter
  // bucket (COLLECTIONS above), not Shopify's product.type field — this is
  // hardcoded theme copy, same category as the filter button labels in the
  // section markup, so it's outside #142 (which is only about raw Shopify
  // logic-field values). Left untouched on purpose.
  // #143 round 3: the bucket KEYS ('Stickers'/'Washi'/'Cards') stay logic
  // (filter state, data-type); only the words a customer reads go through
  // LT(). LT is a hoisted function declaration further down, and these run
  // at render time, after the bridge has been parsed.
  function typeDisplay(label) {
    if (label === 'Stickers') return LT('type_sticker_sheet', 'Sticker sheet');
    if (label === 'Washi') return LT('type_washi_tape', 'Washi tape');
    if (label === 'Cards') return LT('type_postcard', 'Postcard');
    return label;
  }
  // tile meta line ("Sticker · €3.49"): used to be label.slice(0, 7), which
  // happens to give 'Sticker'/'Washi'/'Cards' in English but would chop a
  // translated word mid-way. Same three outputs, now as whole words.
  function typeShort(label) {
    if (label === 'Stickers') return LT('type_short_stickers', 'Sticker');
    if (label === 'Washi') return LT('type_washi', 'Washi');
    if (label === 'Cards') return LT('type_cards', 'Cards');
    return String(label).slice(0, 7);
  }
  // #142 (2026-09-21, Ian): the fixed string below is read from the locale
  // via the data-lt-type-labels JSON bridge (section-lt-build-a-pack.liquid)
  // instead of being hardcoded, so it follows Dutch once nl.json exists.
  // The comparison against the raw type 'Greeting Card' is logic; untouched.
  function itemTypeDisplay(p) {
    if (p.ptype === 'Greeting Card') return ltPackEnvelope || 'Greeting card with tulip envelope';
    return typeDisplay(p.type);
  }

  // Bundle detection: name heuristic plus a price ratio (bundles cost ~2x the
  // cheapest design in the same product). Bundles are shown as rows with a
  // "counts as N" chip; N comes from UNITS (lt-pack-units.json) or titleUnits.
  var COMBO_RE = /^(all|both)\b|complete/i;

  // #31: quantity-pack design rows ("5 Cards", "10 Cards", "5 Postcards",
  // "10 Postcards") are hidden from the builder. They double-dip: bulk-pack
  // pricing already discounts the per-unit cost, and inside the builder they
  // would also trigger tier discounts (10% at 5 units, 15% at 10 units) on top
  // of that. The builder ladder rewards quantity via the tier — no need to also
  // let the buyer buy a pre-bundled quantity pack. These variants keep selling
  // normally on the product pages; this filter is builder-only.
  // Pattern: a number, a space, then "Cards" or "Postcards" (plural, no singular).
  var QUANTITY_PACK_RE = /^\d+\s+(Cards|Postcards)$/i;
  var COMBO_PRICE_RATIO = 1.8;
  var UNITS = {};             // variantId -> units (loaded from lt-pack-units.json)
  // #196: variant ids flagged as quantity packs in lt-pack-units.json (key
  // "quantityPack": [ids]). The English regex above only matches the English
  // title, but on /nl products.json serves "5 kaarten" / "10 ansichtkaarten".
  // The generator classifies from the English catalog, so the id list is
  // language-proof; the regex stays as a fallback for a product added before
  // the generator reran.
  var QTY_PACK_IDS = {};
  var UNITS_URL = root.dataset.unitsUrl || '';

  // Same fallback rule as the Function's unitsForLine() and the generator —
  // keep the three in sync.
  function titleUnits(value) {
    var m = value.match(/\ball\s+(\d{1,2})\b/i) || value.match(/^(\d{1,2})\s/);
    if (m) return Math.max(1, parseInt(m[1], 10));
    if (/\bboth\b/i.test(value)) return 2;
    if (/ \+ | & /.test(value)) return 2;
    return 1;
  }

  function unitsForVariant(variantId, designLabel) {
    var mf = UNITS[String(variantId)];
    if (mf >= 1) return mf;
    return titleUnits(designLabel || '');
  }

  // localized money formatter (falls back to symbol + fixed 2)
  var fmt;
  try {
    var nf = new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: CUR });
    // #197 item 4: Dutch Intl output puts a (non-breaking) space after the symbol
    // ("€ 2,99") while the drawer and cart, which use the shop's money format, show
    // "€2,99". Dropping that one space makes the builder match them in both languages;
    // English output has no such space, so it is untouched.
    fmt = function (cents) { return nf.format(cents / 100).replace(/^(\D+?)[\s\u00a0]+(?=\d)/, '$1'); };
  } catch (e) {
    fmt = function (cents) { return SYM + (cents / 100).toFixed(2); };
  }

  // ---- element refs -----------------------------------------------
  var $ = function (sel, ctx) { return (ctx || root).querySelector(sel); };
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || root).querySelectorAll(sel)); };

  // #68 finding 1: shared focus-trap helpers for the two quick-look dialogs.
  // Mirrors lt-pack-cart.js's "rudimentary focus trap" (Tab cycles within
  // the panel while open) — the drawer's proven approach, reused here rather
  // than inventing a new one.
  function focusableIn(container) {
    if (!container) return [];
    // #116 r2 QA-vondst (Vera): elementen die op dit viewport display:none zijn
    // (zoals de mobiel-only sluit-X, #89) tellen hier mee als "laatste
    // focusable", maar de browser kan er niet op focussen — Tab stapte dus de
    // modal uit zonder dat de wrap ooit triggerde. Alleen zichtbare elementen
    // tellen: offsetParent is null bij display:none (de knoppen zelf zijn niet
    // fixed, dus dit is hier een betrouwbare zichtbaarheidstest).
    return $$('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', container)
      .filter(function (el) { return el.offsetParent !== null; });
  }
  function trapTabIn(container, e) {
    var items = focusableIn(container);
    if (!items.length) return;
    var first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  var searchEl = $('[data-lt-search]');
  var typeBtns = $$('[data-lt-types] [data-type]');
  var motifWrap = $('[data-lt-motifs]');
  var countEl = $('[data-lt-count]');
  var gridEl = $('[data-lt-grid]');
  var emptyEl = $('[data-lt-empty]');
  var errorEl = $('[data-lt-error]');
  var retryBtn = $('[data-lt-retry]');

  var modal = $('[data-lt-modal]');
  var mCard = $('.lt-bap__card'); // #68 finding 1: the dialog itself — focus target + Tab-trap boundary
  var mImg = $('[data-lt-modal-img]');
  var mName = $('[data-lt-modal-name]');
  // #34-followup: the CATEGORY KICKER, the TYPE · PRICE meta line and the multi-
  // design RANGE line are back (Nick's 2026-07-18 restructure). The kicker is
  // motif-driven (resolveMotif) and hides itself when there is no motif; the
  // per-product blurb for SINGLES stays gone (#28 — it was noise).
  var mKicker = $('[data-lt-modal-kicker]');
  var mMeta = $('[data-lt-modal-meta]');
  var mRange = $('[data-lt-modal-range]');
  var mToggle = $('[data-lt-modal-toggle]');
  var mMats = $('[data-lt-modal-materials]');
  var mPicker = $('[data-lt-modal-picker]');
  // #30/#34-followup: the "Add both/all designs" button is gone (markup + JS,
  // Nick 2026-07-17). Rows are MULTI-select now: each theme is added individually
  // and gets its own [− N +] stepper, so an explicit add-all is unnecessary (and
  // it produced nonsense on overlapping rows like a "Both Themes" bundle beside
  // its singles). See toggleDesign() + the mVariants row builder in renderQuick().
  var mVariants = $('[data-lt-modal-variants]');
  var mDone = $('[data-lt-modal-done]');
  // #34: single-design quick-look quantity stepper (shown once the design is in
  // the pack). Multi-design products edit qty per row from the cart drawer, so
  // this stepper is single-design only.
  var mQty = $('[data-lt-modal-qty]');
  var mQtyVal = $('[data-lt-modal-qty-val]');
  var mQtyInc = $('[data-lt-modal-qty-inc]');
  var mQtyDec = $('[data-lt-modal-qty-dec]');

  var kitEl = $('[data-lt-kit]');
  var nudgeEl = $('[data-lt-nudge]');
  var nudgePricesEl = $('[data-lt-nudge-prices]');
  var nudgeImgEl = $('[data-lt-nudge-img]');
  var addJournalBtn = $('[data-lt-add-journal]');
  var dismissJournalEls = $$('[data-lt-dismiss-journal]');   // #50: opt-out (mobile × + desktop "Not now")
  var jrestoreEl = $('[data-lt-jrestore]');                  // #50 fix (D1): quiet restore line shown when the nudge is dismissed
  var jrowEl = $('[data-lt-jrow]');
  var jboxEl = $('[data-lt-jbox]');
  var jrowImgEl = $('[data-lt-jrow-img]');
  var jTitleEl = $('[data-lt-jtitle]');
  var jFromEl = $('[data-lt-jfrom]');
  var jNoteEl = $('[data-lt-jnote]');
  var jColourNameEl = $('[data-lt-jcolourname]');
  var materialsEl = $('[data-lt-materials]');
  var swatchesEl = $('[data-lt-swatches]');
  var removeJournalBtn = $('[data-lt-remove-journal]');
  var insertLineEls = $$('[data-lt-insert-line]');           // #50: nudge insert copy line(s)
  var coverEls = $$('[data-lt-cover]');                       // kit banner only now (#50)

  // #50 journal quick-look modal (desktop only)
  var jmodal = $('[data-lt-jmodal]');
  var jmCard = $('.lt-bap__jcard'); // #68 finding 1: journal dialog — focus target + Tab-trap boundary
  var jmImg = $('[data-lt-jmodal-img]');
  var jmInpack = $('[data-lt-jmodal-inpack]');
  var jmName = $('[data-lt-jmodal-name]');
  var jmPrice = $('[data-lt-jmodal-price]');
  var jmFrom = $('[data-lt-jmodal-from]');
  var jmNote = $('[data-lt-jmodal-note]');
  var jmInsert = $('[data-lt-jmodal-insert]');
  var jmMaterials = $('[data-lt-jmodal-materials]');
  var jmSwatches = $('[data-lt-jmodal-swatches]');
  var jmColourName = $('[data-lt-jmodal-colourname]');
  var jmToggle = $('[data-lt-jmodal-toggle]');
  var journalOpenEls = $$('[data-lt-journal-open]');

  var countLabelEl = $('[data-lt-countlabel]');
  var savingEl = $('[data-lt-saving]');
  var statusEl = $('[data-lt-status]'); // #68 finding 8: composed live-region announcement
  var fillEl = $('[data-lt-fill]');
  var totalEl = $('[data-lt-total]');
  var fullEl = $('[data-lt-full]');
  var addPackBtn = $('[data-lt-add-pack]');
  var fineNoteEl = $('[data-lt-finenote]');

  // #34 mobile bottom-sheet tray elements (all optional — desktop ignores them).
  var trayEl = $('[data-lt-tray]');
  var pillEl = $('[data-lt-pill]');
  var pillLabelEl = $('[data-lt-pill-label]');
  var pillFillEl = $('[data-lt-pill-fill]');
  var scrimEl = $('[data-lt-scrim]');
  var thumbsEl = $('[data-lt-thumbs]');
  var sheetSavingEl = $('[data-lt-sheet-saving]');
  var trayCloseEls = $$('[data-lt-tray-close]');
  // #149 skipped-designs notice: two copies of one message. The first sits
  // in the tray right above the pack progress (desktop bar + open mobile
  // sheet); the second floats above the mobile pill, because on a phone the
  // sheet starts closed and a notice inside it would never be seen.
  var presetNoticeEls = $$('[data-lt-pnotice]');
  var presetNoticeFloatEl = $('[data-lt-pnotice-float]');

  // #116 gift-with-purchase (#192: the set of four freebies). No state of its own
  // — render() writes it straight from c (see below), so these are just the DOM handles.
  var giftEl = $('[data-lt-gift]');
  var giftTextEl = $('[data-lt-gift-text]');
  var giftCheckEl = $('[data-lt-gift-check]');

  // #116 round 2: the gift window (reuses the quick-look modal's
  // OWN pattern — focusableIn/trapTabIn helpers, lastFocus capture/restore —
  // see the markup comment in section-lt-build-a-pack.liquid for the full
  // "why a new dialog, not a literal reuse" reasoning).
  var giftOpenEls = $$('[data-lt-gift-open]');
  var giftModal = $('[data-lt-giftmodal]');
  var giftCard = $('.lt-bap__giftcard'); // focus target + Tab-trap boundary, same role as mCard/jmCard
  var freebieImgEls = $$('[data-lt-giftmodal] img, [data-lt-gift-open] img');   // #192: the freebie set photo Liquid rendered (window + bar thumbnail)

  // ---- journal data + colour map ----------------------------------
  // Known colour names -> swatch hex. Covers the live "Refillable Leather
  // Notebook Cover" variants plus the original design palette as fallback.
  var COLOR_HEX = {
    'brown': '#5f3a29', 'olive green': '#6b7345', 'camel brown': '#c9a084', 'terracotta red': '#b8553f',
    'white sky': '#e8e4da', 'ocean blue': '#4a6d8c', 'red wine': '#722f37', 'midnight black': '#2b2b2b',
    'cognac': '#a05a2c', 'chestnut': '#5f3a29', 'black': '#2b2b2b', 'forest': '#2f4636',
    'sand': '#cdb89a', 'clay': '#b5745c', 'slate': '#5c6670', 'sage': '#8a9a7b'
  };
  var journalData = null;
  // #192: true when the reward product has exactly ONE variant (the coloring book).
  // Then there is nothing to choose: no material/colour controls (Liquid leaves
  // them out of the markup), and the name shown is the product title, not
  // "<material> journal · <colour>". Set in parseJournal() below.
  var J_SINGLE = false;
  var jMaterials = [];   // [{ name, colors:[{name,hex,variantId,price,available}] }]
  var jState = { material: 0, color: 0 };
  // #142 round 2: product-type display labels, read once from the
  // data-lt-type-labels script tag (section-lt-build-a-pack.liquid). Absent
  // node / bad JSON -> both stay at their no-op default and itemTypeDisplay()
  // falls back to the hardcoded English literal, i.e. today's behaviour.
  var ltTypeLabels = null;
  var ltPackEnvelope = '';
  // #149: the skipped-designs notice copy (approved on #147), read from the
  // same script tag. { one, other, all, close }. Absent -> the notice simply
  // never shows: no English fallback here, so the approved locale strings
  // stay the only copy of these words.
  var ltPresetNoticeStrings = null;
  // #143: same bridge, extended with a flat "strings" map for every hardcoded
  // English literal this file writes into the DOM at runtime (dynamic re-
  // renders that Liquid's initial paint doesn't reach). LT() below is the
  // single lookup point — absent/blank map -> the literal fallback, i.e.
  // today's behaviour, same contract as ltTypeLabels/ltPackEnvelope.
  var ltStrings = {};
  (function parseTypeLabels() {
    // #143 round 3: the builder page renders TWO [data-lt-type-labels] tags —
    // cart-drawer.liquid's (overlay group, BEFORE <main>) and this builder's.
    // A bare querySelector returns the drawer's one first, so this file read
    // the drawer's string map and every builder-only key fell back to
    // English on /nl. Ask for our own tag by owner; the bare selector stays
    // as a fallback for any page that only has one bridge.
    // #149 (merged with main 2026-09-28): the same duplicate tag also hid the
    // skipped-designs notice on the storefront, because presetNotice lives
    // only in the builder's tag. The #149 fix round had solved that with its
    // own marker attribute; main's owner attribute (data-lt-bridge="bap")
    // does the same job, so this is now the one way the builder finds its
    // strings. presetNotice is still trusted from the builder's own tag
    // only: if we had to fall back to "any tag" (which on a real page is the
    // drawer's), the notice stays hidden rather than showing wrong words.
    var own = document.querySelector('[data-lt-type-labels][data-lt-bridge="bap"]');
    var node = own || document.querySelector('[data-lt-type-labels]');
    if (!node) return;
    var parsed;
    try { parsed = JSON.parse(node.textContent); } catch (e) { return; }
    if (parsed && parsed.types) ltTypeLabels = parsed.types;
    if (parsed && parsed.packEnvelope) ltPackEnvelope = parsed.packEnvelope;
    if (parsed && parsed.strings) ltStrings = parsed.strings;
    if (own && parsed && parsed.presetNotice) ltPresetNoticeStrings = parsed.presetNotice;
  })();
  function LT(key, fallback) {
    var v = ltStrings[key];
    if (!v) return fallback;
    // Vera B2 (2026-09-26): Shopify's `t` filter HTML-escapes every non-_html locale
    // value before `json` wraps it, so "you've" arrived as "you&#39;ve" and textContent
    // showed the entity literally. Decode once here; _html keys are real HTML already.
    if (typeof v !== 'string' || v.indexOf('&') === -1 || /_html$/.test(key)) return v;
    var ta = document.createElement('textarea');
    ta.innerHTML = v;
    return ta.value;
  }
  (function parseJournal() {
    var node = document.querySelector('[data-lt-journal-data]');
    if (!node) return;
    try { journalData = JSON.parse(node.textContent); } catch (e) { return; }
    if (!journalData || !journalData.variants || !journalData.variants.length) { journalData = null; return; }
    J_SINGLE = journalData.variants.length === 1;
    var opts = journalData.options || [];
    // Two shapes supported:
    //  a) separate Material + Colour options (values 'leather'/'vegan' identify material)
    //  b) one combined option like "Leather - Brown" / "Vegan Leather - Ocean Blue" (the live product)
    var matIdx = -1, colIdx = 0;
    if (opts.length > 1) {
      journalData.variants.forEach(function (v) {
        (v.options || []).forEach(function (val, i) {
          var low = String(val).toLowerCase();
          if (matIdx === -1 && (low.indexOf('leather') > -1 || low.indexOf('vegan') > -1)) matIdx = i;
        });
      });
      if (matIdx === -1) matIdx = 0;
      colIdx = matIdx === 0 ? 1 : 0;
    }

    var matMap = {};
    var order = [];
    journalData.variants.forEach(function (v) {
      var vo = v.options || [];
      var matName, colName;
      if (opts.length > 1) {
        matName = vo[matIdx] != null ? String(vo[matIdx]) : 'Journal';
        colName = vo[colIdx] != null ? String(vo[colIdx]) : (v.title || 'Default');
      } else {
        var raw = vo[0] != null ? String(vo[0]) : (v.title || 'Default');
        var sep = raw.indexOf(' - ');
        if (sep > -1) { matName = raw.slice(0, sep); colName = raw.slice(sep + 3); }
        else { matName = 'Journal'; colName = raw; }
      }
      if (!matMap[matName]) { matMap[matName] = { name: matName, colors: [] }; order.push(matName); }
      matMap[matName].colors.push({
        name: colName,
        hex: COLOR_HEX[colName.toLowerCase()] || '#5f3a29',
        variantId: v.id,
        price: typeof v.price === 'number' ? v.price : parseInt(v.price, 10) || 0,
        available: v.available !== false,
        // #50: real per-variant Shopify photo (falls back to the product image,
        // then null → the hatch placeholder shows). All 8 live variants have one.
        image: v.image || journalData.image || null
      });
    });
    jMaterials = order.map(function (k) { return matMap[k]; });

    // #50: warm the browser cache for every variant photo so a material/colour
    // swap paints the new image with no flash. The frame owns the geometry, so
    // even an un-cached swap can't jump the layout — this just smooths the paint.
    (function preloadJournalImages() {
      if (typeof Image !== 'function') return;
      jMaterials.forEach(function (m) {
        m.colors.forEach(function (col) {
          if (!col.image) return;
          try { var im = new Image(); im.src = journalImgUrl(col.image, 256); } catch (e) { /* preload is best-effort */ }
        });
      });
    })();
  })();

  // ---- state ------------------------------------------------------
  var state = {
    selected: {},        // variantId -> { pid, label, price, image }
    search: '',
    type: 'all',
    motif: 'All',
    journalAdded: false,
    journalKit: false,
    journalDismissed: false,   // #50: nudge opt-out for the rest of the session (persisted via the #52 draft store)
    journalQuickOpen: false,   // #50: desktop journal quick-look modal open?
    quickPid: null,
    editToken: null,     // #34 edit mode: set from ?edit=<token>; CTA → "Update pack"
    productParam: null,  // i45 item 2: set from ?product=<handle>; consumed once after catalog load
    packParam: null,     // #148: raw ?pack=<entries> value; consumed once after catalog load (applyPackFromLink)
    presetResult: null,  // #148: resolvePresetPack's { picks, skips, rows } once a pack link was applied; #149's notice reads the skips
    presetNoticeDismissed: false,   // #149: shopper closed the notice; this page view only (never stored, the pack param is gone after a reload anyway)
    trayOpen: false,     // #34 mobile: is the bottom-sheet tray expanded?
    // #68 finding 1: mirrors the drawer's state.lastFocus (lt-pack-cart.js) —
    // captured the instant a modal opens, restored the instant it closes.
    quickLastFocus: null,
    journalLastFocus: null,
    // #116 round 2: same lastFocus pattern as the two dialogs above, for the
    // gift window.
    giftLastFocus: null
  };
  // #104: subscribers for the desktop-tray panel's own render sync (own file,
  // lt-bap-desktop-tray.js) — see the ltBapDesktopApi block near the end of
  // this IIFE for why this exists. Declared here (with the other state) so it
  // is guaranteed initialized before the first render() call at the bottom.
  var desktopTraySubscribers = [];
  var products = [];     // normalized catalog
  var byId = {};         // pid -> product
  var byVariant = {};    // #34: variantId -> { p, d } for edit-mode preload
  var tiles = [];        // tile elements (same order as products)
  var deepLinkTile = null; // #46 polish: ?product deep-link target, re-scrolled once at quick-look close
  // #34-followup: the motif chips the builder actually shows (configured list, or
  // auto-built from tags). The quick-look CATEGORY KICKER reuses THIS list so the
  // kicker names the exact category the chip filter would file the product under —
  // no second source, no invented motif. buildMotifs() fills it; resolveMotif()
  // reads it. Empty until the catalog loads.
  var motifChips = [];
  // pin_tag / pin_badge section settings: read once. pinBadge arrives through the
  // section's data attribute, so Shopify's own setting translation (Translate &
  // Adapt) applies to it exactly like every other section text.
  var pinKey = (root.dataset.pinTag || '').trim().toLowerCase();
  var pinBadge = (root.dataset.pinBadge || '').trim();
  var motifChipEls = []; // #69 P3-10: chip <button> elements, cached at buildMotifs() like tiles/typeBtns — render() re-queries this list on every keystroke/click otherwise

  // #52: draft persistence (sessionStorage, not localStorage — a days-old
  // draft must never resurrect a stale session). draftReady flags the moment
  // the post-catalog restore pass has run, so the pre-catalog render() calls
  // below never clobber a stored draft with the empty startup state before
  // restoreDraft() has had a chance to read it.
  var DRAFT_KEY = 'lt-bap-draft';
  var draftReady = false;

  // #69 P1-3: catalog cache. Every builder visit used to fetch 3 full
  // collections' worth of products.json (body_html, every image, every
  // variant) even though normalizeProduct() only keeps ~10 fields per
  // product. Cache the NORMALIZED result (never the raw payload) in
  // sessionStorage so a same-session revisit (nudge link, back button,
  // re-opening the tab) skips the refetch entirely. Bump the version
  // segment of the key whenever normalizeProduct()'s output shape changes,
  // so an old cached shape from a previous deploy never gets fed to code
  // that expects new fields.
  var CATALOG_CACHE_KEY = 'lt-bap-catalog-v1';
  var CATALOG_TTL_MS = 15 * 60 * 1000; // 15 min

  // preset journal-kit via URL (#journal or ?kit)
  (function () {
    // #107: this used to substring-match /journal|kit/ against the WHOLE
    // hash+query, so a PDP deep-link like ?product=journal-in-a-magical-…
    // silently put the leather journal in the pack. Kit intent must be
    // explicit: the #journal hash itself, or a real kit query parameter.
    var hash = (window.location.hash || '').toLowerCase();
    var q = new URLSearchParams(window.location.search || '');
    if ((hash === '#journal' || q.has('kit')) && journalData) { state.journalAdded = true; state.journalKit = true; }
  })();

  // #34 edit mode: ?edit=<token> loads that pack from the cart into the builder.
  // i45 item 2 (2026-07-20): ?product=<handle> preselects the design a
  // customer arrived from via the PDP "Add to a pack" CTA. Both just capture
  // here — the actual lookups need the catalog, so they run after
  // loadCatalog() resolves (preloadPack / preselectProduct below).
  (function () {
    try {
      var params = new URLSearchParams(window.location.search || '');
      var tok = params.get('edit');
      if (tok) {
        state.editToken = tok;
        // #52: edit mode wins over any stored draft outright. Clear it now
        // (not just skip restoring it) so dropping ?edit= from the URL later
        // never resurrects a stale draft from before the edit started.
        try { sessionStorage.removeItem(DRAFT_KEY); } catch (e2) { /* storage unavailable — nothing to clear */ }
      }
      var prod = params.get('product');
      if (prod) state.productParam = prod;
    } catch (e) { /* URLSearchParams unsupported — edit mode / deep link simply stay off */ }
  })();

  // #148 preset pack link: ?pack=<entry>,<entry>,... Captured here, applied
  // once after the catalog loads (applyPackFromLink). We keep the RAW,
  // still percent-encoded value instead of URLSearchParams.get(): the
  // resolver does its own forgiving decoding, and decoding twice would turn
  // an encoded "%252C" into a real comma. Same value the link checker
  // (tools/pack-link-check.mjs) feeds the resolver, so checker and shopper
  // see the same pack.
  function isPackSegment(seg) {
    var key = seg.split('=')[0];
    try { key = decodeURIComponent(key); } catch (e) { /* odd key — compare it as written */ }
    return key === 'pack';
  }
  (function () {
    var segs = (window.location.search || '').replace(/^\?/, '').split('&');
    for (var i = 0; i < segs.length; i++) {
      if (!isPackSegment(segs[i])) continue;
      var eq = segs[i].indexOf('=');
      var raw = eq === -1 ? '' : segs[i].slice(eq + 1);
      if (raw) state.packParam = raw;   // "?pack=" with nothing after it = no pack
      break;                            // first ?pack= wins, like URLSearchParams.get()
    }
  })();

  // #148: after a pack link has been applied, take `pack` out of the address
  // bar (no navigation, no new history entry). A reload then shows the
  // shopper's own swaps from the draft store instead of re-applying the
  // link on top of them. Every other parameter (utm_*, kit, ...) and the
  // #hash stay exactly as they were written.
  function dropPackParam() {
    try {
      var kept = (window.location.search || '').replace(/^\?/, '').split('&')
        .filter(function (seg) { return seg && !isPackSegment(seg); });
      var url = window.location.pathname + (kept.length ? '?' + kept.join('&') : '') + (window.location.hash || '');
      window.history.replaceState(window.history.state, '', url);
    } catch (e) { /* no history API — worst case a reload applies the link again */ }
  }

  // ---- catalog fetch + normalization ------------------------------
  function imgUrl(src, w) {
    if (!src) return '';
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  // #28: request an UNCROPPED image (width only). Some source URLs from the
  // CDN carry a baked-in square crop as a filename suffix (…_380x380_crop_center.jpg)
  // or as crop=/height= query params. Strip both forms so the modal always asks
  // the CDN for the WHOLE image at the requested width. The media frame is a
  // fixed-geometry crop window (object-fit:cover, Nick 2026-07-18 — supersedes
  // the #28 contain/letterbox rule), so the CSS does the cropping: starting from
  // the whole photo means we crop it once, centered, at full resolution, instead
  // of re-cropping a baked-in low-res square centre-crop. The grid tiles keep
  // using imgUrl() directly (square cover is fine there).
  function fullImgUrl(src, w) {
    if (!src) return '';
    // drop a "_<W>x<H>_crop_<pos>" (or "_<W>x<H>") suffix before the extension
    var noCrop = src.replace(/_\d+x\d+(_crop_[a-z]+)?(?=\.[a-z]+(\?|$))/i, '');
    // drop crop/height query params that would force a fixed aspect ratio
    noCrop = noCrop.replace(/([?&])(crop|height)=[^&]*/gi, '$1').replace(/[?&]+$/, '').replace(/&&+/g, '&').replace(/\?&/, '?');
    return imgUrl(noCrop, w);
  }

  // #50: journal photos. On the live store the src is a Shopify CDN URL, so we
  // add a width param for a right-sized fetch (like the tiles). A data: URI
  // (used by the offline preview harness) must be passed through untouched —
  // appending "?width=" would corrupt it — so short-circuit those.
  function journalImgUrl(src, w) {
    if (!src) return '';
    if (src.slice(0, 5) === 'data:') return src;
    return imgUrl(src, w);
  }

  function stripHtml(html) {
    var div = document.createElement('div');
    div.innerHTML = html || '';
    var text = (div.textContent || '').replace(/\s+/g, ' ').trim();
    var words = text.split(' ');
    return words.length > 42 ? words.slice(0, 42).join(' ') + '…' : text;
  }

  function cents(p) { return Math.round(parseFloat(p) * 100) || 0; }

  // Yes/No axes ("Include Butterfly Washi Tape?") read badly as bare rows.
  function rowLabel(axisName, value) {
    if (/^(yes|no)$/i.test(value)) {
      var thing = axisName.replace(/^include\s*/i, '').replace(/\?$/, '').trim();
      // #143: prefix words -> LT(); `thing` is the merchant's own option-name
      // text (product data), left untouched.
      return (/^yes$/i.test(value)
        ? LT('with_thing', 'With {thing}').replace('{thing}', thing)
        : LT('without_thing', 'Without {thing}').replace('{thing}', thing));
    }
    return value;
  }

  // Fills QTY_PACK_IDS from the loaded units JSON. Tolerates the old file shape
  // (no "quantityPack" key) and a failed fetch ({} / null). Called on EVERY
  // catalog load so flags from an earlier load never linger.
  function setQuantityPackIds(unitsJson) {
    QTY_PACK_IDS = {};
    var list = unitsJson && unitsJson.quantityPack;
    if (!Array.isArray(list)) return;
    list.forEach(function (id) { QTY_PACK_IDS[String(id)] = true; });
  }

  // A design row is a quantity pack when its (English) value matches the regex
  // OR any of its variants is flagged in the units JSON. "Any", not "all":
  // the generator flags every material variant of the row, but one flagged
  // variant is already proof the row is a bulk pack.
  function isQuantityPackDesign(value, design) {
    if (QUANTITY_PACK_RE.test(value)) return true;
    return Object.keys(design.byMat).some(function (m) { return QTY_PACK_IDS[String(design.byMat[m].id)]; });
  }

  function normalizeProduct(p, label) {
    var opts = p.options || [];
    var real = opts.filter(function (o) { return !(o.name === 'Title' && o.values && o.values[0] === 'Default Title'); });
    var matOpt = null, designOpt = null;
    real.forEach(function (o) {
      if (!matOpt && /material/i.test(o.name)) matOpt = o;
      else if (!designOpt) designOpt = o;
    });

    // design value + material value per variant
    var designMap = {};   // value -> { value, label, byMat: { mat: cell } }
    var designOrder = [];
    (p.variants || []).forEach(function (v) {
      var dVal = designOpt ? String(v['option' + designOpt.position]) : '';
      var mVal = matOpt ? String(v['option' + matOpt.position]) : '';
      if (!designMap[dVal]) {
        designMap[dVal] = { value: dVal, label: rowLabel(designOpt ? designOpt.name : '', dVal), byMat: {} };
        designOrder.push(dVal);
      }
      designMap[dVal].byMat[mVal] = {
        id: v.id,
        price: cents(v.price),
        available: v.available !== false,
        image: v.featured_image && v.featured_image.src ? v.featured_image.src : null
      };
    });

    // #31: remove quantity-pack rows before any further processing.
    // A product whose remaining designs collapse to one becomes single-variant
    // (multi:false, no badge, direct toggle on the tile) — same as a TITLE-only
    // product. The filtered designs never enter state.selected, so the tier
    // metafields / Function heuristics for those variants simply become inert.
    designOrder = designOrder.filter(function (k) { return !isQuantityPackDesign(k, designMap[k]); });

    function designMin(d) {
      var min = Infinity;
      Object.keys(d.byMat).forEach(function (m) { if (d.byMat[m].price < min) min = d.byMat[m].price; });
      return min;
    }
    var globalMin = Infinity;
    designOrder.forEach(function (k) { var m = designMin(designMap[k]); if (m < globalMin) globalMin = m; });

    // classify bundle rows ("Both Themes", "All 6 Washi Tapes", "Complete … Set")
    // as bundles — shown with a "counts as N" chip in the picker
    var designs = designOrder.map(function (k) { return designMap[k]; });
    designs.forEach(function (d) {
      d.isBundle = designOrder.length > 1 &&
        (COMBO_RE.test(d.value) || designMin(d) >= globalMin * COMBO_PRICE_RATIO);
    });
    var singles = designs.filter(function (d) { return !d.isBundle; });

    // materials present on kept designs, in option order
    var matNames = [];
    if (matOpt) {
      matOpt.values.forEach(function (m) {
        var used = designs.some(function (d) { return d.byMat[m]; });
        if (used) matNames.push(m);
      });
    } else {
      matNames = [''];
    }

    // default material: first with any available cell
    var matIdx = 0;
    for (var i = 0; i < matNames.length; i++) {
      if (designs.some(function (d) { var c = d.byMat[matNames[i]]; return c && c.available; })) { matIdx = i; break; }
    }

    var tags = (p.tags || []).map(function (t) { return String(t); });
    var soldout = designs.every(function (d) {
      return matNames.every(function (m) { var c = d.byMat[m]; return !c || !c.available; });
    });

    return {
      id: p.id,
      handle: p.handle,   // i45 item 2: keys the ?product=<handle> deep link
      title: p.title,
      type: label,
      ptype: p.product_type || '', // 2026-09-17: real type; drives itemTypeDisplay
      // #34-followup: the design-axis name ("Theme" / "Set" / "Washi Tape" /
      // "Sticker Sheet" / named). Drives the noun in the multi RANGE line so it
      // reads honestly per product ("themes" only when the axis really is themes;
      // "designs" otherwise) instead of calling a washi range "themes".
      designAxis: designOpt ? String(designOpt.name) : '',
      tags: tags,
      tagset: tags.join('|').toLowerCase(),
      motif: tags[0] || p.product_type || label,
      image: p.images && p.images[0] ? p.images[0].src : null,
      desc: stripHtml(p.body_html),
      designs: designs,
      singles: singles,
      materials: matNames,
      matIdx: matIdx,
      multi: designs.length > 1,
      badge: designs.length > 1 ? (singles.length > 1 ? singles.length + ' ' + LT('badge_designs', 'designs') : designs.length + ' ' + LT('badge_options', 'options')) : '',
      soldout: soldout,
      search: (p.title + ' ' + designs.map(function (d) { return d.label; }).join(' ') + ' ' + tags.join(' ')).toLowerCase()
    };
  }

  function fetchCollection(label, handle) {
    var all = [];
    function page(n) {
      return fetch(ROOT_URL + 'collections/' + encodeURIComponent(handle) + '/products.json?limit=250&page=' + n, { headers: { 'Accept': 'application/json' } })
        .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.json(); })
        .then(function (j) {
          var ps = j.products || [];
          all = all.concat(ps);
          return ps.length === 250 ? page(n + 1) : all;
        });
    }
    // #196: returns the RAW products. Normalising needs the units JSON (the
    // quantity-pack flags), which loads in parallel, so fetchAndMergeCatalog()
    // normalises once both have landed.
    return page(1);
  }

  // #69 P1-3: the actual network round-trip — 3 collections' products.json +
  // the units file, merged/deduped/normalized. Factored out of loadCatalog()
  // so both the cold-visit path and the background cache-refresh path share
  // exactly one implementation.
  function fetchAndMergeCatalog() {
    var unitsReq = UNITS_URL
      ? fetch(UNITS_URL, { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : {}; })
          .catch(function () { return {}; })
      : Promise.resolve({});
    return Promise.all([unitsReq].concat(COLLECTIONS.map(function (c) { return fetchCollection(c[0], c[1]); })))
      .then(function (results) {
        var unitsMap = results[0] || {};
        // #196: flags first, then normalise (normalizeProduct reads them).
        setQuantityPackIds(unitsMap);
        var lists = results.slice(1).map(function (raw, li) {
          return raw.map(function (p) { return normalizeProduct(p, COLLECTIONS[li][0]); });
        });
        // #63: merge the three collections by fractional position instead of
        // concatenating, so the default 'All' grid mixes types (washi and
        // cards spread through the stickers) rather than showing 100+ stickers
        // first. Sort is stable on position, so the order WITHIN each type is
        // unchanged and the per-type filtered views render exactly as before.
        var merged = [];
        // 2026-09-18 (Nick): kaarten iets eerder in de All-grid, wel gemengd.
        var TYPE_WEIGHT = [1.0, 1.0, 0.7];
        lists.forEach(function (list, li) {
          var w = TYPE_WEIGHT[li] || 1.0;
          list.forEach(function (p, i) { merged.push({ p: p, pos: ((i + 0.5) / list.length) * w }); });
        });
        merged.sort(function (a, b) { return a.pos - b.pos; });
        var seen = {};
        var out = [];
        merged.forEach(function (entry) {
          var p = entry.p;
          // #63: tag lt-bap-exclude keeps a product out of the builder without
          // touching its collections (Mystery Bag / Oopsie Bag stay purchasable
          // on the normal collection pages).
          if (seen[p.id] || !p.designs.length || p.tagset.indexOf('lt-bap-exclude') !== -1) return;
          seen[p.id] = true;
          out.push(p);
        });
        return { products: out, units: unitsMap };
      });
  }

  // #69 P1-3: cheap fingerprint of a normalized catalog — count + id order.
  // Used to decide whether a background-refreshed catalog actually changed
  // enough to justify re-rendering (a no-op refresh must not reset scroll
  // position, close an open quick-look, or otherwise disturb the shopper).
  function catalogFingerprint(list) {
    return list.length + ':' + list.map(function (p) { return p.id; }).join(',');
  }

  function readCatalogCache() {
    try {
      var raw = sessionStorage.getItem(CATALOG_CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.products || !parsed.fingerprint || typeof parsed.savedAt !== 'number') return null;
      return parsed;
    } catch (e) { return null; } // storage unavailable / corrupt entry — treat as no cache
  }

  function writeCatalogCache(list, unitsMap) {
    try {
      sessionStorage.setItem(CATALOG_CACHE_KEY, JSON.stringify({
        savedAt: Date.now(),
        units: unitsMap,
        products: list,
        fingerprint: catalogFingerprint(list)
      }));
    } catch (e) { /* storage full/unavailable — cache is a nice-to-have, not required */ }
  }

  // Populates the module-level catalog state (products/byId/byVariant/UNITS)
  // from an already-merged, already-normalized list, then rebuilds the tiles
  // and motif chips. Shared by every path that lands a catalog (cache hit,
  // cold fetch, or background refresh).
  function applyCatalog(list, unitsMap) {
    UNITS = unitsMap || {};
    products = [];
    byId = {};
    byVariant = {};
    // Pinned seasonal block (section setting pin_tag, e.g. "christmas-2026"): every
    // product carrying that tag moves to the front of the merged list as ONE block,
    // keeping the card/washi/sheet mix the fractional merge gave it; everything else
    // follows in its existing order. Done HERE, not in fetchAndMergeCatalog(), on
    // purpose: the session cache stores the merged order, so pinning at read time
    // means a changed setting (or a cache written before the setting existed) takes
    // effect immediately and CATALOG_CACHE_KEY needs no bump. Empty setting = no-op.
    if (pinKey) {
      var pinned = [], others = [];
      list.forEach(function (p) { (productHasAnyTag(p, [pinKey]) ? pinned : others).push(p); });
      list = pinned.concat(others);
    }
    list.forEach(function (p) {
      products.push(p);
      byId[p.id] = p;
      // #34: index every material-cell variant id back to its product +
      // design, so edit mode can rebuild a selection from cart variant ids.
      p.designs.forEach(function (d) {
        Object.keys(d.byMat).forEach(function (m) { byVariant[d.byMat[m].id] = { p: p, d: d }; });
      });
    });
    buildTiles();
    buildMotifs();
  }

  // Runs exactly once, right after the FIRST catalog lands (cache hit or cold
  // fetch): restores/deep-links selection state and does the first paint.
  // Must never run again on a background refresh — that would clobber
  // whatever the shopper has already selected.
  function firstRenderPass() {
    // #52: edit token wins outright (preloadPack loads the exact cart pack);
    // otherwise restore any stored draft. restoreDraft() runs BEFORE
    // preselectProduct() so a ?product= deep link composes with the
    // restored draft rather than replacing it (spec: restore first, deep
    // link behavior unchanged).
    // #148: read edit mode BEFORE preloadPack, because preloadPack can drop
    // the token on its own (no cart helper); ?edit= in the link must still
    // win over ?pack= in that case.
    var editing = !!state.editToken;
    if (editing) preloadPack(state.editToken);
    else restoreDraft();
    // #148: a preset pack link fills the builder INSTEAD of the ?product=
    // deep link (spec #145: ?pack= wins over ?product=; ?edit= wins over
    // ?pack=). Without ?pack= this is exactly the old path.
    if (state.packParam && !editing) applyPackFromLink();
    else preselectProduct();
    draftReady = true;   // #52: safe to persist again now that the one-time restore pass has run
    render();            // #148: this render also saves the applied pack as the draft
  }

  // #148: fill the builder from ?pack=. Runs once, from firstRenderPass
  // above, so a background catalog refresh can never re-apply it over the
  // shopper's swaps. The rules live in two pure functions further down:
  // resolvePresetPack (link -> which designs) and applyPresetPack (designs ->
  // selection, replacing the restored draft, leaving the journal alone).
  // The cart is not touched here; the shopper still clicks "Add pack to cart".
  function applyPackFromLink() {
    var raw = state.packParam;
    state.packParam = null;
    state.productParam = null;   // ?pack= wins: the ?product= quick-look does not open
    var resolved = resolvePresetPack(raw, products);
    state.selected = applyPresetPack(state, resolved, products, selectionEntry).selected;
    state.presetResult = resolved;   // kept for #149's skipped-designs notice
    dropPackParam();
  }

  function loadCatalog() {
    errorEl.hidden = true;
    var cached = readCatalogCache();
    var isFresh = cached && (Date.now() - cached.savedAt) < CATALOG_TTL_MS;

    if (isFresh) {
      // #69 P1-3: valid cache within the 15-min TTL — render straight from
      // it. Zero catalog network fetch; this is the whole point of the cache.
      applyCatalog(cached.products, cached.units);
      firstRenderPass();
      return;
    }

    if (cached) {
      // Cache exists but is past its TTL: render instantly from the stale
      // copy (better than a loading skeleton for a repeat visitor) while
      // refreshing underneath. Only touch the DOM again if the refreshed
      // catalog actually differs from what's already on screen.
      applyCatalog(cached.products, cached.units);
      firstRenderPass();
      fetchAndMergeCatalog()
        .then(function (fresh) {
          writeCatalogCache(fresh.products, fresh.units);
          if (catalogFingerprint(fresh.products) !== cached.fingerprint) {
            applyCatalog(fresh.products, fresh.units);
            render();
          }
        })
        .catch(function () { /* background refresh failed — keep serving the stale render, no user-facing error */ });
      return;
    }

    // Cold visit: no usable cache at all — original behavior, skeleton +
    // blocking fetch.
    gridEl.setAttribute('aria-busy', 'true');
    fetchAndMergeCatalog()
      .then(function (fresh) {
        writeCatalogCache(fresh.products, fresh.units);
        applyCatalog(fresh.products, fresh.units);
        firstRenderPass();
      })
      .catch(function () {
        $$('[data-lt-skel]', gridEl).forEach(function (s) { s.remove(); });
        errorEl.hidden = false;
      })
      .then(function () { gridEl.setAttribute('aria-busy', 'false'); });
  }

  // ---- pricing helpers --------------------------------------------
  function cellFor(p, d, matIdx) {
    var m = p.materials[matIdx != null ? matIdx : p.matIdx];
    var c = d.byMat[m];
    if (c) return c;
    // design not offered in this material — fall back to its first cell
    for (var i = 0; i < p.materials.length; i++) { if (d.byMat[p.materials[i]]) return d.byMat[p.materials[i]]; }
    return null;
  }

  function tilePriceLabel(p) {
    // price the tile off the single designs; bundles are priced in the picker
    var base = p.singles.length ? p.singles : p.designs;
    var prices = base.map(function (d) { var c = cellFor(p, d); return c ? c.price : 0; });
    var min = Math.min.apply(null, prices);
    var allSame = prices.every(function (x) { return x === min; });
    if (!p.multi || base.length === 1) return fmt(min);
    return allSame ? LT('price_each', '{price} each').replace('{price}', fmt(min)) : LT('price_from', 'from {price}').replace('{price}', fmt(min));
  }

  // #34-followup: the price label for the quick-look TYPE · PRICE line. Priced off
  // the single designs (bundles are priced per-row in the picker). "each" appears
  // only when the product is multi-design AND every single shares one price — so a
  // Reading Time range reads "€2.79 each" while a mixed-price range reads "from €X"
  // and a plain single reads just "€X".
  function quickPriceLabel(p) {
    var base = p.singles.length ? p.singles : p.designs;
    var prices = base.map(function (d) { var c = cellFor(p, d); return c ? c.price : 0; });
    var min = Math.min.apply(null, prices);
    var allSame = prices.every(function (x) { return x === min; });
    if (!p.multi || base.length === 1) return fmt(min);
    return allSame ? LT('price_each', '{price} each').replace('{price}', fmt(min)) : LT('price_from', 'from {price}').replace('{price}', fmt(min));
  }

  // #34-followup: the noun for the multi RANGE line. Honest per product: only a
  // real "Theme" (or named-theme) axis is called "themes"; every other multi axis
  // (Set, Washi Tape, Sticker Sheet, Time Format, With/Without) is called
  // "designs", which is true of any selectable row. Singular when count is 1.
  function rangeNoun(p, count) {
    var axis = String(p.designAxis || '').toLowerCase();
    var isTheme = /theme/.test(axis);
    if (count === 1) return isTheme ? LT('range_noun_theme_singular', 'theme') : LT('range_noun_design_singular', 'design');
    return isTheme ? LT('range_noun_theme_plural', 'themes') : LT('range_noun_design_plural', 'designs');
  }

  // units the selected rows of this product contribute toward the tiers.
  // #34: a selection contributes units*qty (a "counts as 2" bundle at qty 3 = 6).
  function selectedCountFor(p) {
    var n = 0;
    Object.keys(state.selected).forEach(function (k) {
      var e = state.selected[k];
      if (e.pid === p.id) n += (e.units || 1) * (e.qty || 1);
    });
    return n;
  }

  function findSelectedKey(p, d) {
    // a design is "on" if any of its material variants is selected
    var key = null;
    Object.keys(d.byMat).forEach(function (m) { if (state.selected[d.byMat[m].id]) key = d.byMat[m].id; });
    return key;
  }

  function selectionEntry(p, d, c) {
    return {
      pid: p.id,
      label: p.multi ? p.title + ' · ' + d.label : p.title,  // #47 em-dash sweep (house rule): customer-facing tray/quick-look label
      price: c.price,
      units: unitsForVariant(c.id, d.value),
      qty: 1,              // #34: per-design quantity (>=1); entry is deleted at 0
      image: (c && c.image) || p.image || null   // #34: for the mobile thumb grid
    };
  }

  // #34 per-design quantity. Every selection carries a qty (>=1). Tier math and
  // the stationery subtotal both multiply by qty, so one variant added twice
  // counts as 2 items (and, for a bundle worth N units, as 2*N items). These two
  // helpers change qty in place on an already-selected variant; adding a NOT-yet-
  // selected variant goes through toggleDesign()/setMaterial() as before.
  function incQty(variantId) {
    var e = state.selected[variantId];
    if (!e) return;
    e.qty = (e.qty || 1) + 1;
    render();
  }
  function decQty(variantId) {
    var e = state.selected[variantId];
    if (!e) return;
    e.qty = (e.qty || 1) - 1;
    if (e.qty <= 0) delete state.selected[variantId];   // - at 1 removes
    render();
  }

  // #34-followup MULTI-SELECT (Plan Task 2 / C5, supersedes the #30 radio model):
  // per-theme rows are now MULTI-selectable. Each design of a product can be in
  // the pack independently; a multi-design range like "Reading Time" can carry
  // BOTH its themes at once, each as its own sheet counting toward the tier. This
  // replaces the old "one design per product" radio semantics — the plan requires
  // it (each row's price swaps for a [− N +] stepper once added; NO add-both
  // button, Nick 2026-07-17).
  //
  // toggleDesign flips ONE design on/off WITHOUT touching sibling designs:
  //  - off  -> add it at qty 1 (if the cell is available);
  //  - on   -> remove it (used by the single-design "Remove from pack" toggle and
  //            by a row's − at qty 1; qty>1 decrements via decQty instead).
  // Single-design products have exactly one design, so this is a plain toggle for
  // them; multi-design products get true multi-select because we never clear
  // siblings.
  function toggleDesign(p, d) {
    var onKey = findSelectedKey(p, d);
    if (onKey) { delete state.selected[onKey]; render(); return; }
    var c = cellFor(p, d);
    if (!c || !c.available) return;
    state.selected[c.id] = selectionEntry(p, d, c);
    render();
  }

  function setMaterial(p, idx) {
    if (idx === p.matIdx) return;
    p.matIdx = idx;
    // re-key selected designs of this product onto the new material's variants
    p.designs.forEach(function (d) {
      var onKey = findSelectedKey(p, d);
      if (!onKey) return;
      var keepQty = state.selected[onKey].qty || 1;   // #34: carry qty across the swap
      delete state.selected[onKey];
      var c = cellFor(p, d);
      if (c && c.available) {
        var entry = selectionEntry(p, d, c);
        entry.qty = keepQty;
        state.selected[c.id] = entry;
      }
    });
    refreshTileMeta(p);   // price label can differ per material — refresh just this tile
    render();
  }

  // Copy prices must match checkout to the cent (Nick 2026-07-18). Two rules:
  //
  // TIERS (pack subtotal): the Function emits a bare percentage; Shopify's
  // engine takes pct% of the price in cents and FLOORS the discount amount
  // before subtracting (observed live 2026-07-19). Keep this floor, not taste.
  function engineNet(priceCents, pct) {
    return priceCents - Math.floor(priceCents * pct / 100);
  }

  // JOURNAL: since 2026-07-24 (#50, Nick: keep the .99/.49 endings) the
  // Function emits a FIXED amount with the discount cents CEILed
  // (49.99 at 10% → 5.00 off → 44.99; vegan 44.99 → 4.50 off → 40.49),
  // so the display ceils too. Only the journal reward works this way.
  function journalEngineNet(priceCents, pct) {
    return priceCents - Math.ceil(priceCents * pct / 100);
  }

  function compute() {
    var count = 0, stationeryFull = 0;
    Object.keys(state.selected).forEach(function (k) {
      var e = state.selected[k];
      var qty = e.qty || 1;
      count += (e.units || 1) * qty;       // #34: tiers count items = units*qty
      stationeryFull += e.price * qty;      // #34: subtotal is price*qty
    });
    var tier = count >= T2 ? (T2P / 100) : (count >= T1 ? (T1P / 100) : 0);
    // Same floor rule as the journal copy: the engine floors tier% of the pack
    // subtotal in cents (observed live 2026-07-19: €13.86 at 10% → −€1.38,
    // where round-the-net said €12.47 but the cart charges €12.48).
    var stationeryNet = tier > 0 ? engineNet(stationeryFull, tier * 100) : stationeryFull;
    var journalUnlocked = state.journalAdded && count >= J_MIN;
    var col = currentColor();
    var variantPrice = col ? col.price : 0;
    var journalFull = state.journalAdded ? variantPrice : 0;
    var journalNet = state.journalAdded ? (journalUnlocked ? journalEngineNet(variantPrice, J_PCT) : variantPrice) : 0;
    return {
      count: count,
      tier: tier,
      fullTotal: stationeryFull + journalFull,
      total: stationeryNet + journalNet,
      hasDiscount: tier > 0 || journalUnlocked,
      journalUnlocked: journalUnlocked,
      journalFull: journalFull,
      journalNet: journalNet
    };
  }

  // ---- tile building ----------------------------------------------
  function buildTiles() {
    gridEl.innerHTML = '';
    tiles = [];
    var frag = document.createDocumentFragment();
    products.forEach(function (p) {
      var t = document.createElement('article');
      t.className = 'lt-tile' + (p.soldout ? ' lt-tile--soldout' : '');
      t.setAttribute('data-lt-tile', '');
      t.dataset.id = p.id;
      t.dataset.type = p.type;

      var media = '';
      if (p.image) {
        media = '<img class="lt-img-cover" loading="lazy" alt="' + escapeHtml(p.title) + '"' +
          ' src="' + imgUrl(p.image, 360) + '"' +
          ' srcset="' + [240, 360, 480, 720].map(function (w) { return imgUrl(p.image, w) + ' ' + w + 'w'; }).join(', ') + '"' +
          ' sizes="(max-width: 749px) 46vw, (max-width: 1099px) 30vw, 18vw">';
      }
      // #32: the zoom button moves onto the image (bottom-left) so long titles
      // never collide with it. lt-tile__mediaarea is the shared positioning
      // context for the toggle button, all image-overlay badges, and the zoom
      // button. The zoom button is a sibling of the toggle (not nested inside it)
      // so HTML validity is maintained. stopPropagation on the zoom button
      // prevents the click from reaching any ancestor listener that might
      // interpret it as a tile-toggle action — critical for single-design tiles
      // whose entire mediaarea is also the direct-add click target.
      // #68 finding 9: a multi-design tile's toggle OPENS the quick-look dialog
      // (see the gridEl click handler's `if (p.multi) { openQuick(...) }`) — it
      // doesn't toggle selection directly, so aria-pressed is the wrong role
      // here. aria-haspopup/aria-expanded is the pattern already correct at
      // lt-collection-card.liquid:92-94; render() keeps aria-expanded synced.
      // True only when BOTH settings are filled and the product carries the pin tag.
      var pinnedTile = !!(pinBadge && pinKey && productHasAnyTag(p, [pinKey]));
      var toggleAria = p.multi ? 'aria-haspopup="dialog" aria-expanded="false"' : 'aria-pressed="false"';
      t.innerHTML =
        '<div class="lt-tile__mediaarea">' +
          '<button type="button" class="lt-tile__toggle" data-lt-toggle ' + toggleAria + (p.soldout ? ' disabled' : '') + '>' +
            '<span class="lt-tile__media lt-hatch">' + media + '</span>' +
            // Pinned-collection badge ("Winter collection") REPLACES the "N designs" badge
            // on that tile (Nick 2026-10-20: "de 2 designs mag je weghalen als er 'winter
            // collection' staat"). The customer still picks the design in the quick-look, so
            // nothing is lost; the corner just stays calm. Every other tile keeps p.badge.
            (pinnedTile ? '<span class="lt-tile__badge lt-tile__badge--pin">' + escapeHtml(pinBadge) + '</span>' : '') +
            (p.badge && !pinnedTile ? '<span class="lt-tile__badge">' + p.badge + '</span>' : '') +
            (p.soldout ? '<span class="lt-tile__soldout">' + LT('sold_out', 'Sold out') + '</span>' : '') +
            '<span class="lt-tile__check" aria-hidden="true"></span>' +
          '</button>' +
          '<button type="button" class="lt-tile__zoom" data-lt-zoom title="' + LT('quick_look', 'Quick look') + '" aria-label="' + LT('quick_look_aria', 'Quick look: {name}').replace('{name}', escapeHtml(p.title)) + '">⚲</button>' +
          // #34 hover stepper: a camel [- N +] pill that replaces the count bubble
          // on desktop hover of a SELECTED single-design tile. It is a sibling of
          // the toggle (not nested — a <button> can't hold buttons) and its own
          // buttons stopPropagation so clicking +/- never toggles the tile. CSS
          // shows it only on hover of a .is-selected tile with .lt-tile--single.
          '<div class="lt-tile__stepper" data-lt-tile-stepper hidden>' +
            '<button type="button" class="lt-tile__stepbtn" data-lt-tile-dec aria-label="' + LT('fewer', 'Fewer') + '">−</button>' +
            '<span class="lt-tile__stepqty" data-lt-tile-qty>1</span>' +
            '<button type="button" class="lt-tile__stepbtn" data-lt-tile-inc aria-label="' + LT('more', 'More') + '">+</button>' +
          '</div>' +
        '</div>' +
        '<div class="lt-tile__body">' +
          '<div class="lt-tile__name">' + escapeHtml(p.title) + '</div>' +
          '<div class="lt-tile__meta" data-lt-tile-meta>' + escapeHtml(typeShort(p.type) + ' · ' + tilePriceLabel(p)) + '</div>' +
        '</div>';
      // Cache the child refs and the toggle button once, so render() doesn't
      // querySelector four nodes per tile on every keystroke (#14). The price
      // meta is written here once — it only changes on a material swap, which
      // we handle in refreshTileMeta().
      t._check = t.querySelector('.lt-tile__check');
      t._toggle = t.querySelector('[data-lt-toggle]');
      t._meta = t.querySelector('[data-lt-tile-meta]');
      // #34: hover-stepper refs. Only single-design tiles get the pill; multi
      // tiles keep the count bubble (qty lives per-row in the quick-look there).
      t._stepper = t.querySelector('[data-lt-tile-stepper]');
      t._stepqty = t.querySelector('[data-lt-tile-qty]');
      t._single = !p.multi;
      if (t._single) t.classList.add('lt-tile--single');
      frag.appendChild(t);
      tiles.push(t);
    });
    gridEl.appendChild(frag);
  }

  // Refresh one tile's price/meta line after a material swap (the only thing
  // that changes it). Keeps the per-keystroke render() free of price work.
  function refreshTileMeta(p) {
    var idx = products.indexOf(p);
    if (idx < 0 || !tiles[idx] || !tiles[idx]._meta) return;
    tiles[idx]._meta.textContent = typeShort(p.type) + ' · ' + tilePriceLabel(p);
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  // ---- motif chips ------------------------------------------------
  // #2 (BL-1): a chip is a { label, tags:[...] } pair. The section setting maps a
  // human LABEL to the short product TAGS it should match, mirroring the
  // "Label:tag" convention the collection filters adopted 2026-07-19. Syntax:
  //   "Flowers:flower, Reading & Books:book+reading, Holidays:Holidays+christmas"
  // comma separates chips, ":" splits label from its tag list, "+" separates the
  // OR-tags inside one chip. A bare entry with no ":" (e.g. "Japan") treats the
  // label itself as the tag — backward compatible with the old literal-tag list.
  // We deliberately use "+" (not commas) inside a chip so the whole thing still
  // composes with the comma-separated setting — "Label|tag1,tag2" would collide
  // with the chip separator.
  //
  // A product matches a chip if it carries ANY of the chip's tags. Tag URLs on
  // the collection pages can only AND, but the builder filters client-side, so
  // multi-tag OR is free here — that is exactly why several short tags can fold
  // into one label ("book" OR "reading" -> "Reading & Books"). Matching is
  // case-insensitive and EXACT per tag (pipe-delimited lookup below), so "book"
  // never bleeds into "bookshelf".
  function parseChip(entry) {
    var i = entry.indexOf(':');
    var label = (i > -1 ? entry.slice(0, i) : entry).trim();
    var tagSpec = i > -1 ? entry.slice(i + 1) : entry;
    var tags = tagSpec.split('+').map(function (t) { return t.trim().toLowerCase(); }).filter(Boolean);
    return { label: label, tags: tags };
  }

  // exact (case-insensitive) membership: does p carry any of these tags?
  // p.tagset is the tags joined by "|" and lowercased; wrapping it in leading and
  // trailing "|" lets a single indexOf test for a whole tag, not a substring.
  function productHasAnyTag(p, tags) {
    var hay = '|' + p.tagset + '|';
    for (var i = 0; i < tags.length; i++) {
      if (hay.indexOf('|' + tags[i] + '|') > -1) return true;
    }
    return false;
  }

  function buildMotifs() {
    var configured = (root.dataset.motifs || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    var list;
    if (configured.length) {
      list = configured.map(parseChip);
    } else {
      // Auto-build fallback: the most common tags become one-tag chips. Only used
      // when the setting is blank; the shipped default always configures chips.
      var seen = {};
      products.forEach(function (p) {
        p.tags.forEach(function (tag) { seen[tag] = (seen[tag] || 0) + 1; });
      });
      list = Object.keys(seen).sort(function (a, b) { return seen[b] - seen[a]; }).slice(0, 12)
        .map(function (tag) { return { label: tag, tags: [tag.toLowerCase()] }; });
    }
    // #2 (BL-1): drop chips no product matches, so a mislabelled or not-yet-tagged
    // motif hides rather than rendering a dead chip that filters to zero designs.
    list = list.filter(function (chip) {
      return products.some(function (p) { return productHasAnyTag(p, chip.tags); });
    });
    // Store the real motif categories (chips minus the "All" pseudo-chip) so the
    // quick-look kicker resolves against the same list.
    motifChips = list.slice();
    motifWrap.innerHTML = '';
    motifChipEls = [];   // #69 P3-10: rebuilt alongside the DOM below, same lifecycle
    // Featured chip (section setting featured_motif): rendered right after "All"
    // whatever its place in motif_tags. Only the RENDER order changes; motifChips
    // keeps the configured order because resolveMotif() (quick-look kicker) takes
    // the first matching chip in that order. A label that matches no chip never
    // reaches `list` (filtered above), so a featured label without products stays hidden.
    var featuredKey = (root.dataset.featuredMotif || '').trim().toLowerCase();
    var labels = list.map(function (c) { return c.label; });
    var featuredLabel = null;
    if (featuredKey) {
      for (var fi = 0; fi < labels.length; fi++) {
        if (labels[fi].toLowerCase() === featuredKey) { featuredLabel = labels[fi]; break; }
      }
    }
    if (featuredLabel !== null) {
      labels = [featuredLabel].concat(labels.filter(function (l) { return l !== featuredLabel; }));
    }
    ['All'].concat(labels).forEach(function (name) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'lt-bap__chip' + (name === featuredLabel ? ' is-featured' : '') + (name === state.motif ? ' is-active' : '');
      b.setAttribute('aria-pressed', name === state.motif ? 'true' : 'false'); // #68 finding 7
      // 'All' stays the internal filter key (state.motif, dataset.motif, the sentinel
      // checks); only the visible label is localised (#197: NL showed English 'All').
      b.textContent = name === 'All' ? LT('chip_all', 'All') : name;
      b.dataset.motif = name;
      b.addEventListener('click', function () { state.motif = name; render(); });
      motifWrap.appendChild(b);
      motifChipEls.push(b);
    });
  }

  // ---- filtering --------------------------------------------------
  // #2 (BL-1): find the active chip by its label so filtering matches against the
  // chip's mapped tags, not the human label. (motifChips is small — a linear scan
  // is cheaper than maintaining a parallel map.)
  function chipByLabel(label) {
    for (var i = 0; i < motifChips.length; i++) {
      if (motifChips[i].label === label) return motifChips[i];
    }
    return null;
  }

  function productMatches(p) {
    if (state.type !== 'all' && p.type !== state.type) return false;
    if (state.motif !== 'All') {
      var chip = chipByLabel(state.motif);
      // A chip with no products was filtered out of motifChips, so a live chip
      // always resolves here; the guard just keeps a stale state.motif inert.
      if (!chip || !productHasAnyTag(p, chip.tags)) return false;
    }
    if (state.search && p.search.indexOf(state.search) === -1) return false;
    return true;
  }

  // #34-followup: resolve the CATEGORY KICKER for the quick-look. Returns the
  // first configured motif chip this product belongs to (chip order), matched the
  // SAME way the chip filter matches in productMatches() — tag substring OR the
  // product's own motif value. Returns null when no chip matches, and the caller
  // then HIDES the kicker (no empty element, no guessing).
  //
  // Why not just show p.motif? Because in the live catalog p.motif is tags[0],
  // which is almost always the collection slug "all-stationery" (161/166 products
  // in the fixtures) — a slug, not a motif. Feeding that to the kicker would print
  // "ALL-STATIONERY" on nearly every product. The chip is the only honest motif
  // the builder actually resolves, so the kicker uses exactly that.
  // #2 (BL-1): resolve against each chip's mapped TAGS (same as the filter), and
  // return the chip's human LABEL for the kicker.
  function resolveMotif(p) {
    for (var i = 0; i < motifChips.length; i++) {
      if (productHasAnyTag(p, motifChips[i].tags)) return motifChips[i].label;
    }
    return null;
  }

  // ---- rendering --------------------------------------------------
  function applyCover() {
    if (!journalData || !jMaterials.length) return;
    var mat = jMaterials[Math.min(jState.material, jMaterials.length - 1)];
    var col = mat.colors[Math.min(jState.color, mat.colors.length - 1)];
    coverEls.forEach(function (c) { c.style.setProperty('--cover', col.hex); });
  }

  function currentColor() {
    if (!journalData || !jMaterials.length) return null;
    var mat = jMaterials[Math.min(jState.material, jMaterials.length - 1)];
    return mat.colors[Math.min(jState.color, mat.colors.length - 1)] || null;
  }

  function currentVariantId() {
    var col = currentColor();
    return col ? col.variantId : (journalData && journalData.variants[0] && journalData.variants[0].id);
  }

  // #192: how the reward is NAMED (quick-look title, added row, desktop panel row).
  // Several variants (the leather journal): "<material> journal" and " · <colour>",
  // exactly as before. One variant (the coloring book): the product title, and no
  // colour part, since Shopify's "Default Title" is not a colour.
  function journalMatWord(mat) {
    if (J_SINGLE) return journalData.title;
    // regex = logic on the English material name, untouched
    return /journal|notebook|cover/i.test(mat.name) ? mat.name : LT('material_journal', '{material} journal').replace('{material}', mat.name);
  }
  function journalColourSuffix(col) {
    return !J_SINGLE && col && col.name ? ' · ' + col.name : '';
  }

  // #50: render the material tabs + colour swatches into a given pair of
  // containers, reflecting the shared jState. Used by BOTH the added row and the
  // desktop quick-look, so their selectors always stay in sync. The swatch is a
  // ≥40px hit target wrapping a smaller visual dot (design spec); the material
  // tab only renders when the product has more than one material.
  function renderMatSwatches(matsEl, swEl) {
    if (!matsEl || !swEl || !journalData || !jMaterials.length) return;
    var mat = jMaterials[Math.min(jState.material, jMaterials.length - 1)];
    // #68 P0 (same class of bug as the variant rows above): both button rows
    // are wiped and rebuilt on every render() call — a keyboard user tabbing
    // through material/colour tabs would otherwise get dropped to <body>
    // the instant they activate one. This feeds jmCard (in the journal
    // quick-look's focus trap) as well as the always-visible tray row.
    var matFocusIdx = matsEl.contains(document.activeElement)
      ? Array.prototype.indexOf.call(matsEl.children, document.activeElement) : -1;
    var swFocusIdx = swEl.contains(document.activeElement)
      ? Array.prototype.indexOf.call(swEl.children, document.activeElement) : -1;
    matsEl.innerHTML = '';
    matsEl.hidden = jMaterials.length < 2;
    if (jMaterials.length > 1) {
      jMaterials.forEach(function (m, i) {
        var b = document.createElement('button');
        b.type = 'button';
        var matOn = i === jState.material;
        b.className = 'lt-bap__mat' + (matOn ? ' is-active' : '');
        // #68 finding 7 follow-up (Vera): same aria-pressed treatment as the
        // builder type/motif chips — was class-only, so a screen-reader user
        // got no "selected" feedback on the journal's material tabs. Set
        // fresh on every render() rebuild (these buttons are recreated, not
        // patched in place, so there is no separate "sync" step — the
        // creation value below IS the sync).
        b.setAttribute('aria-pressed', matOn ? 'true' : 'false');
        b.textContent = m.name;
        b.addEventListener('click', function () { jState.material = i; jState.color = 0; render(); });
        matsEl.appendChild(b);
      });
    }
    swEl.innerHTML = '';
    mat.colors.forEach(function (c, i) {
      var b = document.createElement('button');
      b.type = 'button';
      var colOn = i === jState.color;
      b.className = 'lt-bap__swatch' + (colOn ? ' is-active' : '');
      b.title = c.name;
      // #68 finding 7 follow-up (Vera): colour swatches get the same
      // treatment — aria-label already named the colour, aria-pressed now
      // carries the selected state too (rebuilt fresh every render()).
      b.setAttribute('aria-pressed', colOn ? 'true' : 'false');
      b.setAttribute('aria-label', c.name);
      var dot = document.createElement('span');
      dot.className = 'lt-bap__swatchdot';
      dot.style.background = c.hex;
      b.appendChild(dot);
      b.addEventListener('click', function () { jState.color = i; render(); });
      swEl.appendChild(b);
    });
    // #68 P0: restore focus captured above (material tab takes priority —
    // if a material change also reshuffled the colour list, landing back on
    // the tab you just picked is the expected/intuitive outcome).
    if (matFocusIdx > -1 && matsEl.children[matFocusIdx]) matsEl.children[matFocusIdx].focus();
    else if (swFocusIdx > -1 && swEl.children[swFocusIdx]) swEl.children[swFocusIdx].focus();
  }

  // #90 founders test root cause (Timothy, live check): every journal photo
  // (nudge/jrow spotlight, quick-look modal) requested at most 384w, but the
  // panels they fill run up to ~920 real (CSS) px on a 3x-DPR phone or ~880 on
  // a 2x desktop — a 2-3x upscale that renders milky/faded. Widened the ladder
  // and — since the three callers share this one function but render at very
  // different CSS widths (108/128px fixed boxes on desktop vs a near-full-bleed
  // mobile spotlight vs the ~440px quick-look column) — added a `sizes` param so
  // the browser's own srcset match picks the right rung per caller/breakpoint
  // instead of guessing off the img's default 100vw.
  var JOURNAL_WIDTHS = [128, 192, 256, 384, 640, 960, 1280];

  // #50: point a fixed-geometry photo frame at a variant's real image. The frame
  // owns the geometry (CSS: absolute-fill, object-fit:cover), so this only swaps
  // the src — it can never resize or shift the block. Empty src → hatch shows.
  function setJournalPhoto(imgEl, src, sizes) {
    if (!imgEl) return;
    if (src) {
      // A data: URI can contain commas (inline SVG), which would break the
      // comma-separated srcset syntax — only build a srcset for CDN URLs.
      if (src.slice(0, 5) === 'data:') {
        imgEl.removeAttribute('srcset');
        imgEl.removeAttribute('sizes');
        imgEl.src = journalImgUrl(src, 384);
      } else {
        // #90 round 4: srcset/sizes BEFORE src, and the src fallback at 1280
        // instead of 384. Sources are proven 2048², srcset carries 1280, and
        // every test engine picks it — yet the founder's device keeps showing
        // a soft photo, consistent with an engine that commits to the small
        // `src` and never re-selects. Browsers that honour srcset ignore the
        // src width entirely, so the only behaviour change is in whatever
        // path was loading the 384 — which is exactly the bug.
        imgEl.srcset = JOURNAL_WIDTHS.map(function (w) { return journalImgUrl(src, w) + ' ' + w + 'w'; }).join(', ');
        imgEl.sizes = sizes || '100vw';
        imgEl.src = journalImgUrl(src, 1280);
      }
      imgEl.hidden = false;
    } else {
      imgEl.removeAttribute('src');
      imgEl.removeAttribute('srcset');
      imgEl.removeAttribute('sizes');
      imgEl.hidden = true;
    }
  }

  // ---- journal quick-look modal (#50, desktop only) ----------------
  // Opened from either journal photo. Shares jState with the added row, so the
  // material/colour selectors here move the row's selection too (and vice-versa).
  // Price is a PREVIEW: it shows the unlocked amount once the pack has J_MIN
  // items, even before the journal is added — always computed, never hard-coded.
  function isMobileViewport() {
    try { return window.matchMedia && window.matchMedia('(max-width: 749px)').matches; }
    catch (e) { return false; }
  }
  // #104 blocker 2: mirrors lt-bap-desktop-tray.js's own isDesktop() boundary
  // exactly (>=990px, matrix row 7's breakpoint) — the width at which the
  // Tray A panel exists and becomes the journal's home (Change -> quick-look).
  // A DISTINCT boundary from isMobileViewport() above (<=749px); this
  // function does not replace it or touch anything isMobileViewport() gates.
  function isDesktopTray() {
    try { return window.matchMedia && window.matchMedia('(min-width: 990px)').matches; }
    catch (e) { return false; }
  }

  // #101 fixronde: replaces plain `.hidden = collapsed` for the nudge/
  // jrestore/jrow wraps. [hidden]'s display:none can't be transitioned, so
  // the CSS side now animates grid-template-rows via .is-collapsed; this
  // toggles that class AND `inert` together so a collapsing/collapsed card
  // drops out of the tab order and the AT tree immediately (same
  // inert-not-hidden pattern the tray already uses, #68 finding 2) while
  // still being allowed to visually animate. Idempotent — render() calls
  // this every re-render with the same computed boolean, so a no-op call
  // (search keystroke, unrelated state change) never re-triggers the
  // transition; only an actual add/remove/dismiss flip does.
  function setCollapsed(el, collapsed) {
    if (!el) return;
    el.classList.toggle('is-collapsed', collapsed);
    el.toggleAttribute('inert', collapsed);
  }
  // #68 findings 2+10: both the tray's `inert` toggle and the journal-photo
  // buttons' disabled state are keyed off isMobileViewport(), which only gets
  // re-read on the next render()/click. A live breakpoint crossing (device
  // rotation, a resized desktop window) would otherwise leave stale state
  // until the shopper next interacts — this listener re-syncs both the
  // instant the 749px boundary is crossed, matching the CSS breakpoint exactly.
  (function watchMobileBreakpoint() {
    journalOpenEls.forEach(function (el) { el.disabled = isMobileViewport(); }); // set the initial state now
    if (!window.matchMedia) return;
    var mq = window.matchMedia('(max-width: 749px)');
    var onChange = function () {
      journalOpenEls.forEach(function (el) { el.disabled = mq.matches; });
      render(); // also re-syncs the tray's `inert` toggle (finding 2) for this breakpoint
    };
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange); // Safari <14
  })();
  // #104 blocker 2: same live-breakpoint-crossing precedent as
  // watchMobileBreakpoint() above, for the SEPARATE 990px boundary — without
  // this, resizing a desktop window with the journal added down through
  // 990px would leave jrowEl (the big configurator card) stuck collapsed
  // (its home, the Tray A panel, has just disappeared) until some unrelated
  // click next called render(). This listener only ever fires on a 990px
  // crossing, a width no mobile shopper reaches, so it cannot affect
  // isMobileViewport()'s <=749px behaviour.
  (function watchDesktopTrayBreakpoint() {
    if (!window.matchMedia) return;
    var mq = window.matchMedia('(min-width: 990px)');
    var onChange = function () { render(); };
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange); // Safari <14
  })();
  function openJournalQuick() {
    if (!jmodal || !journalData || !jMaterials.length) return;
    if (isMobileViewport()) return;   // no journal quick-look on mobile (per the design)
    // #68 finding 1 (P0): same lastFocus capture + post-render focus move as
    // openQuick() above. render() must run BEFORE we query focusableIn(jmCard)
    // — renderJournalQuick() is what actually builds the material/swatch
    // buttons inside it.
    state.journalLastFocus = document.activeElement;
    state.journalQuickOpen = true;
    jmodal.hidden = false;
    document.body.style.overflow = 'hidden';
    render();
    var items = focusableIn(jmCard);
    if (items.length) items[0].focus();
  }
  function closeJournalQuick() {
    if (!jmodal) return;
    state.journalQuickOpen = false;
    jmodal.hidden = true;
    document.body.style.overflow = '';
    // #68 finding 1: return focus to the photo (or restore-line) that opened it.
    // #104 fix: the Tray A panel's Change button (lt-bap-desktop-tray.js) is
    // rebuilt via innerHTML on every render() -- including the very render()
    // openJournalQuick() itself triggers a few lines up -- so when Change was
    // the opener, the node captured in journalLastFocus is already detached
    // by the time we get here; .focus() on a detached node is a silent no-op
    // (verified: focus never returned). Every OTHER opener (the static
    // jphotobtn photo buttons) is real section markup, never rebuilt, so this
    // only ever applies to the panel's button. Re-resolve a live match by its
    // stable [data-act] attribute before falling back to the stale reference.
    var target = state.journalLastFocus;
    if (target && !target.isConnected && target.getAttribute) {
      var act = target.getAttribute('data-act');
      if (act) target = document.querySelector('[data-act="' + act + '"]') || target;
    }
    if (target && target.focus) target.focus({ preventScroll: true });
    state.journalLastFocus = null;
  }

  // #116 round 2: the gift window. Same open/close shape as
  // openQuick()/closeQuick() above (lastFocus capture before the dialog takes
  // over, body scroll lock, first-focusable-child move in, synchronous focus
  // restore on close) — no new pattern invented, just applied to a static
  // dialog instead of a catalog product.
  // #192: the window shows ONE photo of the whole set (Liquid renders it), so
  // there is no front/back state any more. A photo that fails to load (e.g. the
  // file was deleted in Shopify Files) falls back to the hatch box instead of
  // showing a broken-image icon (in the bar thumbnail the hatch is the button's own).
  function freebieFallback(img) {
    var cell = img.parentNode;
    if (!cell) return;
    if (cell.hasAttribute('data-lt-freebie-photo')) cell.setAttribute('data-lt-freebie-placeholder', '');
    img.remove();
  }
  freebieImgEls.forEach(function (img) {
    img.addEventListener('error', function () { freebieFallback(img); });
    if (img.complete && img.naturalWidth === 0 && img.getAttribute('src')) freebieFallback(img);   // failed before this script ran
  });
  function openGiftModal() {
    if (!giftModal) return;
    state.giftLastFocus = document.activeElement;
    giftModal.hidden = false;
    document.body.style.overflow = 'hidden';
    var items = focusableIn(giftCard);
    if (items.length) items[0].focus();
  }
  function closeGiftModal() {
    if (!giftModal) return;
    giftModal.hidden = true;
    document.body.style.overflow = '';
    if (state.giftLastFocus && state.giftLastFocus.focus) state.giftLastFocus.focus({ preventScroll: true });
    state.giftLastFocus = null;
  }

  function renderJournalQuick(c) {
    if (!jmodal || !jMaterials.length) return;
    var mat = jMaterials[Math.min(jState.material, jMaterials.length - 1)];
    var col = mat.colors[Math.min(jState.color, mat.colors.length - 1)] || { name: '', price: 0, image: null };
    // #90: desktop-only quick-look media column — .lt-bap__jcardmedia is
    // 1.05/2.05 of the card (min(860px, 100vw-80px)), i.e. a fixed 440px once
    // the card hits its 860px cap (viewport >= 940px incl. the modal's 40px
    // side padding), scaling with viewport below that.
    setJournalPhoto(jmImg, col.image, '(min-width: 940px) 440px, 51vw');
    if (jmInpack) jmInpack.hidden = !state.journalAdded;
    if (jmName) jmName.textContent = journalMatWord(mat);
    // preview: unlocked once the pack reaches J_MIN, regardless of added state.
    var previewUnlocked = c.count >= J_MIN;
    var previewNet = journalEngineNet(col.price, J_PCT);
    if (jmPrice) jmPrice.textContent = (!J_SINGLE && col.name ? col.name + ': ' : '') + fmt(previewUnlocked ? previewNet : col.price);
    if (jmFrom) { jmFrom.textContent = fmt(col.price); jmFrom.hidden = !previewUnlocked; }
    if (jmNote) {
      jmNote.textContent = previewUnlocked
        ? LT('unlocked_pct_off_marker', "you've unlocked {pct}% off").replace('{pct}', J_PCT)
        : LT('pct_off_unlocks_at', '{pct}% off unlocks at {n} items').replace('{pct}', J_PCT).replace('{n}', J_MIN);
      jmNote.classList.toggle('is-live', previewUnlocked);
    }
    if (jmInsert) {
      if (INSERT_VALUE) { jmInsert.textContent = LT('insert_includes', 'Includes a blank Tomoe River insert (52gsm) ({value} value)').replace('{value}', INSERT_VALUE); jmInsert.hidden = false; }
      else jmInsert.hidden = true;
    }
    renderMatSwatches(jmMaterials, jmSwatches);
    if (jmColourName) jmColourName.textContent = col.name || '';
    if (jmToggle) {
      jmToggle.textContent = state.journalAdded ? LT('remove_from_pack', 'Remove from pack') : LT('add_journal', 'Add journal');
      jmToggle.classList.toggle('is-in', state.journalAdded);
      jmToggle.classList.toggle('lt-bap__jcardbtn--add', !state.journalAdded);
    }
  }

  function render() {
    saveDraft();   // #52: render() is the one place every mutation path already funnels through, so it is the cheapest hook for "persist on every selection change" (guarded internally: no-ops before the restore pass and while an edit is in progress)
    // filter tiles — this runs on every keystroke/click, so it must stay cheap:
    // cached child refs (no per-tile querySelector), no price recompute (the
    // meta line is written once at build and only refreshed on a material swap).
    // #69 P3-9: selectedCountFor(p) used to re-scan the whole state.selected
    // object for EVERY tile (O(tiles × selections), ~180 tiles on the full
    // grid). One pre-pass over the (usually small) selection instead, giving
    // an O(1) lookup per tile below. selectedCountFor() itself is untouched —
    // still used as-is at its other, non-loop call site.
    var countsByPid = {};
    Object.keys(state.selected).forEach(function (k) {
      var e = state.selected[k];
      countsByPid[e.pid] = (countsByPid[e.pid] || 0) + (e.units || 1) * (e.qty || 1);
    });
    var visible = 0;
    tiles.forEach(function (t, i) {
      var p = products[i];
      var show = productMatches(p);
      t.hidden = !show;
      if (show) visible++;
      var n = countsByPid[p.id] || 0;
      t.classList.toggle('is-selected', n > 0);
      // #34 bubble content:
      //  - multi tile: the selected design's unit count (unchanged).
      //  - single tile: ✓ at qty 1, the number at qty >1 (the design spec).
      // For a single-design product n IS the qty (units=1), so n doubles as the
      // qty shown in both the bubble (>1) and the hover-stepper pill.
      if (t._check) t._check.textContent = n === 0 ? '' : (p.multi ? String(n) : (n > 1 ? String(n) : '✓'));
      if (t._stepqty && n > 0) t._stepqty.textContent = String(n);
      // The pill is togglable by CSS on hover, but keep it out of the a11y tree /
      // tab order when the tile is not selected: hidden unless selected+single.
      if (t._stepper) t._stepper.hidden = !(t._single && n > 0);
      // #68 finding 9: multi tiles get aria-expanded (dialog open/closed for
      // THIS product), not aria-pressed (see the toggleAria comment above).
      if (t._toggle) {
        if (p.multi) {
          t._toggle.setAttribute('aria-expanded', (state.quickPid === p.id && !modal.hidden) ? 'true' : 'false');
        } else {
          t._toggle.setAttribute('aria-pressed', n > 0 ? 'true' : 'false');
        }
      }
    });
    emptyEl.hidden = visible !== 0 || !products.length;

    // count label + motif suffix
    var motifSuffix = state.motif === 'All' ? '' : LT('showing_motif_suffix', ' in {motif}').replace('{motif}', state.motif);  // 'All' is the logic sentinel, untouched
    countEl.textContent = products.length
      ? LT('showing_designs_full', 'Showing {n} {noun}{motif}')
          .replace('{n}', visible)
          .replace('{noun}', visible === 1 ? LT('showing_designs_word_singular', 'design') : LT('showing_designs_word_plural', 'designs'))
          .replace('{motif}', motifSuffix)
      : '';

    // motif chip active state
    // #69 P3-10: cached at buildMotifs() instead of re-querying the DOM on every render()
    motifChipEls.forEach(function (c) {
      var on = c.dataset.motif === state.motif;
      c.classList.toggle('is-active', on);
      c.setAttribute('aria-pressed', on ? 'true' : 'false'); // #68 finding 7
    });
    // type btn active
    typeBtns.forEach(function (b) {
      var on = b.dataset.type === state.type;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false'); // #68 finding 7
    });

    // pricing
    var c = compute();
    countLabelEl.textContent = c.count === 0 ? LT('tap_to_start', 'Tap designs to start your pack')
      : LT('count_selected', '{n} {noun} selected')
          .replace('{n}', c.count)
          .replace('{noun}', c.count === 1 ? LT('item_singular', 'item') : LT('item_plural', 'items'));
    var saving = '';
    if (c.count === 0) saving = '';
    else if (c.count < T1) saving = LT('tier_progress_t1', 'Add {n} more for {pct}% off').replace('{n}', T1 - c.count).replace('{pct}', T1P);
    else if (c.count < T2) saving = LT('tier_progress_t2', '{pct1}% off · add {n} more for {pct2}%').replace('{pct1}', T1P).replace('{n}', T2 - c.count).replace('{pct2}', T2P);
    else saving = LT('tier_progress_unlocked', '{pct}% off unlocked').replace('{pct}', T2P);
    savingEl.textContent = saving;
    savingEl.classList.toggle('is-live', c.tier > 0);
    // #68 finding 8: one composed message ("6 items selected, 10% off unlocked"),
    // not aria-live on the two visible spans above — setting the SAME string
    // again is a no-op for the live region (no re-announcement), so this is
    // safe to run on every render() without spamming a screen reader mid-typing.
    if (statusEl) statusEl.textContent = countLabelEl.textContent + (saving ? ', ' + saving : '');

    var pct = Math.min(100, (c.count / T2) * 100);
    fillEl.style.width = pct + '%';
    fillEl.classList.toggle('is-max', c.tier >= (T2P / 100));

    // #116 gift-with-purchase (#192: four freebies): reads c.count, the SAME compute()
    // result the tier fill/badge line right above just used — never a second,
    // parallel count. T2 is the same constant everywhere else on this page
    // (parsed once from data-t2 on the section root), so there is no second
    // "10" anywhere in this decision.
    if (giftEl && giftTextEl) {
      var giftUnlocked = c.count >= T2;
      giftEl.classList.toggle('is-unlocked', giftUnlocked);
      if (giftCheckEl) giftCheckEl.hidden = !giftUnlocked;
      var giftMsg;
      if (giftUnlocked) {
        giftMsg = LT('freebie_set_unlocked_msg', 'Your four exclusive winter freebies are included, free with your pack.');
      } else if (c.count === 0) {
        giftMsg = LT('freebie_set_locked_msg', 'Build a full pack and these four exclusive winter freebies come with it.');
      } else {
        var giftLeft = T2 - c.count;
        giftMsg = LT('freebie_set_countdown', '{n} more {noun} to unlock your four freebies.')
          .replace('{n}', giftLeft)
          .replace('{noun}', giftLeft === 1 ? LT('item_singular', 'item') : LT('item_plural', 'items'));
      }
      giftTextEl.textContent = giftMsg;
      // #68 finding 8's own pattern, applied here too (impeccable Assessment
      // A caught the gap): the composed status region above never included
      // the gift line, so a screen-reader user heard the tier progress but
      // never learned the free gift existed or unlocked. Same
      // dedup-safe append, same live region — no second aria-live element.
      if (statusEl) statusEl.textContent += ', ' + giftMsg;
    }

    totalEl.textContent = fmt(c.total);
    if (c.hasDiscount) { fullEl.hidden = false; fullEl.textContent = fmt(c.fullTotal); }
    else { fullEl.hidden = true; }

    addPackBtn.disabled = c.count === 0;
    // #34 edit mode: the CTA reads "Update pack" instead of "Add pack to cart".
    // Only relabel when not mid-flight (addPackToCart temporarily shows Adding…/
    // Updating…), which it guards by disabling the button before the fetch.
    if (!addPackBtn.disabled || c.count === 0) {
      addPackBtn.textContent = state.editToken ? LT('update_pack', 'Update pack') : LT('add_pack_to_cart', 'Add pack to cart');
    }
    fineNoteEl.hidden = !c.hasDiscount;

    // #50 journal upsell v2 states
    if (journalData) {
      if (kitEl) kitEl.hidden = !state.journalKit;

      // #50: the nudge insert copy line is static (only the value binds).
      if (INSERT_VALUE) {
        insertLineEls.forEach(function (el) {
          el.textContent = LT('insert_includes', 'Includes a blank Tomoe River insert (52gsm) ({value} value)').replace('{value}', INSERT_VALUE);
          el.hidden = false;
        });
      } else {
        insertLineEls.forEach(function (el) { el.hidden = true; });
      }

      // State A: nudge. Hidden once added, below the unlock threshold, OR
      // dismissed for the session (#50 opt-out — never reappears on re-render or
      // tier change; only removing/hiding the row is unaffected).
      if (nudgeEl) {
        setCollapsed(nudgeEl, state.journalAdded || c.count < J_MIN || state.journalDismissed);
        if (jMaterials.length && jMaterials[0].colors.length) {
          // Hero is always Leather - Brown (first material, first colour): its
          // real photo and its from/to prices (never hard-coded).
          var hero = jMaterials[0].colors[0];
          // #90: fixed 108px box on desktop; at <=749px .lt-bap__jphoto--nudge
          // goes full-bleed (width:100%) inside the tray's .lt-wrap, which has
          // 20px padding each side — real width is the viewport minus that 40px.
          setJournalPhoto(nudgeImgEl, hero.image, '(max-width: 749px) calc(100vw - 40px), 108px');
          if (nudgePricesEl) {
            nudgePricesEl.innerHTML = '· <span class="lt-bap__jfrom">' + escapeHtml(fmt(hero.price)) +
              '</span> ' + escapeHtml(fmt(journalEngineNet(hero.price, J_PCT)));
          }
        }
      }

      // #50 fix (D1): the quiet restore line takes the nudge's place ONLY when the
      // nudge would otherwise show (5+ items, not added) but has been dismissed.
      // Clicking it (below) clears the flag and brings the full nudge — and every
      // add path — back. This is the sole add route on mobile after a dismiss.
      if (jrestoreEl) setCollapsed(jrestoreEl, !(state.journalDismissed && !state.journalAdded && c.count >= J_MIN));

      // State B: added row.
      if (jrowEl) {
        // #104 blocker 2 (Vera 2026-08-17 / ties to #101): on the Tray A
        // desktop path (>=990px) the journal's home is the panel row (Change
        // -> quick-look) once it's in the pack — this big in-flow
        // configurator card (photo, material tabs, swatches, REMOVE) must
        // never re-expand there, or the journal shows twice. Below 990px
        // (mobile AND the 750-989px gap, which has no panel) the original
        // expand-on-add behaviour is untouched: isDesktopTray() is false
        // there, so this reduces to the exact original `!state.journalAdded`
        // expression — byte-identical outcome for every mobile viewport.
        setCollapsed(jrowEl, !state.journalAdded || isDesktopTray());
        if (state.journalAdded) {
          renderMatSwatches(materialsEl, swatchesEl);
          var mat = jMaterials[Math.min(jState.material, jMaterials.length - 1)] || { name: 'Journal', colors: [{ name: '' }] };
          var col = mat.colors[Math.min(jState.color, mat.colors.length - 1)] || { name: '', image: null };
          // #90: same mobile full-bleed math as the nudge photo above; fixed
          // 128px box on desktop (.lt-bap__jphoto--row).
          setJournalPhoto(jrowImgEl, col.image, '(max-width: 749px) calc(100vw - 40px), 128px');
          // Title carries the REAL material + colour + price ("Leather journal ·
          // Brown: €44.99"). Only add the word "journal" when the material name
          // isn't already a notebook noun (avoids "Journal journal" on a
          // single-material fallback product).
          jTitleEl.textContent = journalMatWord(mat) + journalColourSuffix(col) + ': ' + fmt(c.journalUnlocked ? c.journalNet : c.journalFull);
          if (jFromEl) { jFromEl.textContent = fmt(c.journalFull); jFromEl.hidden = !c.journalUnlocked; }
          jNoteEl.textContent = c.journalUnlocked
            ? LT('unlocked_pct_off_marker', "you've unlocked {pct}% off").replace('{pct}', J_PCT)
            : LT('pct_off_unlocks_at', '{pct}% off unlocks at {n} items').replace('{pct}', J_PCT).replace('{n}', J_MIN);
          jNoteEl.classList.toggle('is-live', c.journalUnlocked);
          if (jColourNameEl) jColourNameEl.textContent = col.name || '';
          jboxEl.classList.toggle('is-live', c.journalUnlocked);
        }
      }

      // Desktop journal quick-look (only re-rendered while open).
      if (state.journalQuickOpen) renderJournalQuick(c);

      applyCover();   // kit banner cover tint only (#50: nudge/row use real photos now)
    }

    // #34 mobile bottom-sheet tray (pill + sheet + thumbnails)
    renderMobileTray(c);

    // #149: after the pill, because the floating notice parks itself above
    // the pill and needs to know whether the pill is showing.
    renderPresetNotice();

    // modal in sync
    if (state.quickPid != null) renderQuick();

    // #104: notify the desktop-tray panel (own file) after every render() —
    // see the ltBapDesktopApi block near the end of this IIFE.
    desktopTraySubscribers.forEach(function (cb) { cb(); });
  }

  // #34 Mobile bottom sheet. The CSS only styles these on <=749px, so on desktop
  // this just toggles hidden flags on elements that are display:none there — no
  // visual effect. The pill shows when a pack has items and the sheet is closed;
  // the sheet (the tray with .is-open) shows the thumbnail grid + all the normal
  // tray content. Tapping a thumbnail removes that design from the pack.
  function renderMobileTray(c) {
    if (!pillEl || !trayEl) return;   // section without the mobile markup
    var has = c.count > 0;

    // collapsed pill: visible only when items exist AND sheet closed
    pillEl.hidden = !(has && !state.trayOpen);
    if (has) {
      if (pillLabelEl) {
        var tierTxt = c.tier > 0 ? LT('pill_tier_on', '{pct}% off').replace('{pct}', Math.round(c.tier * 100))
          : LT('pill_tier_progress', '{n} more for {pct}%').replace('{n}', T1 - c.count).replace('{pct}', T1P);
        // separators (' · ' and the wider '  ·  ') are punctuation, not words
        pillLabelEl.textContent = c.count + ' ' + (c.count === 1 ? LT('item_singular', 'item') : LT('item_plural', 'items')) +
          ' · ' + fmt(c.total) + '  ·  ' + tierTxt;
      }
      if (pillFillEl) pillFillEl.style.width = Math.min(100, (c.count / T2) * 100) + '%';
    }

    // sheet open/closed
    trayEl.classList.toggle('is-open', state.trayOpen);
    // #68 finding 2: on mobile the closed tray was only moved off-screen via
    // transform — every control inside it stayed tab-reachable and visible
    // to a screen reader. `inert` skips it the instant it closes; the CSS
    // (section stylesheet, .lt-bap__tray) adds a delayed visibility:hidden
    // once the slide-out transition finishes. Desktop's tray is always the
    // visible bar (state.trayOpen never gates it there), so inert only ever
    // applies on the mobile breakpoint.
    trayEl.toggleAttribute('inert', isMobileViewport() && !state.trayOpen);
    if (scrimEl) scrimEl.hidden = !state.trayOpen;

    // mirror the saving line into the sheet header
    if (sheetSavingEl) {
      sheetSavingEl.textContent = savingEl.textContent;
      sheetSavingEl.classList.toggle('is-live', c.tier > 0);
    }

    // thumbnail grid — one square per selected design, with a ×N qty badge.
    if (thumbsEl) {
      thumbsEl.innerHTML = '';
      Object.keys(state.selected).forEach(function (k) {
        var e = state.selected[k];
        var qty = e.qty || 1;
        var cell = document.createElement('button');
        cell.type = 'button';
        cell.className = 'lt-bap__thumb';
        cell.dataset.variant = k;
        cell.title = LT('remove_line_aria_full', 'Tap to remove: {name}').replace('{name}', e.label);
        // #46: the ×N qty badge lives at the BUTTON level, NOT inside
        // .lt-bap__thumbimg. That wrapper is overflow:hidden (it clips the image
        // to rounded corners), which cut the badge's -6px corner overhang so the
        // count wasn't fully visible on the tight mobile thumbs. The button is
        // position:relative, so the badge still pins to the image's top-right
        // corner — now rendered in full.
        cell.innerHTML =
          '<span class="lt-bap__thumbimg lt-hatch">' +
            (e.image ? '<img class="lt-img-cover" alt="' + escapeHtml(e.label) + '" src="' + imgUrl(e.image, 180) + '">' : '') +
          '</span>' +
          '<span class="lt-bap__thumbbadge">×' + qty + '</span>' +
          '<span class="lt-bap__thumbname">' + escapeHtml(e.label) + '</span>';
        thumbsEl.appendChild(cell);
      });
    }
  }

  function openTray() { state.trayOpen = true; render(); }
  function closeTray() { state.trayOpen = false; render(); }

  // #149: the words for a presetNotice() answer. Pick the plural form in JS
  // (the count only exists here) and fill in the number. The section passes
  // the strings through Shopify's `t` filter with a "[count]" stand-in, the
  // pattern Shopify's own Dawn theme uses for numbers only JS knows; we also
  // accept a raw "{{ count }}" in case `t` hands the placeholder through
  // untouched. English and Dutch both use "one" for exactly 1 only.
  function presetNoticeText(notice, strings) {
    if (!notice || !strings) return '';
    if (notice.variant === 'all') return strings.all || '';
    var tpl = notice.count === 1 ? strings.one : strings.other;
    if (!tpl) return '';
    return String(tpl).replace(/\{\{\s*count\s*\}\}|\[count\]/g, String(notice.count));
  }

  // #149: put presetNotice()'s answer on screen, in both copies (see the
  // markup comment in section-lt-build-a-pack.liquid). Called from every
  // render(), so it only touches the page when something actually changes:
  // the text is written only when it differs (each write to a role="status"
  // region is read out again by screen readers), and a page without a pack
  // link never gets a single attribute changed.
  function renderPresetNotice() {
    if (!presetNoticeEls.length) return;
    var notice = state.presetNoticeDismissed ? null : presetNotice(state.presetResult);
    var text = presetNoticeText(notice, ltPresetNoticeStrings);
    var on = !!text;
    presetNoticeEls.forEach(function (el) {
      var textEl = el.querySelector('[data-lt-pnotice-text]');
      var closeEl = el.querySelector('[data-lt-pnotice-close]');
      if (!on && !el.classList.contains('is-on')) return;   // never shown: leave the markup exactly as served
      el.classList.toggle('is-on', on);
      if (textEl && textEl.textContent !== text) textEl.textContent = text;
      if (closeEl) {
        closeEl.hidden = !on;
        if (on && ltPresetNoticeStrings.close) closeEl.setAttribute('aria-label', ltPresetNoticeStrings.close);
      }
    });
    if (!presetNoticeFloatEl || !on) return;
    // Mobile only (the CSS never shows the floating copy above 749px). While
    // the sheet is open the tray copy is on screen instead, so the floating
    // one steps aside. Otherwise it sits 10px above the pill when the pill
    // shows (the pill's own bottom offset + its height), or where the pill
    // would be when the pack is empty ("This pack isn't available").
    presetNoticeFloatEl.classList.toggle('is-sheet-open', !!state.trayOpen);
    var bottom = '';
    if (pillEl && !pillEl.hidden && pillEl.offsetHeight) {
      var pillBottom = parseFloat(window.getComputedStyle(pillEl).bottom) || 0;
      bottom = Math.round(pillBottom + pillEl.offsetHeight + 10) + 'px';
    }
    presetNoticeFloatEl.style.bottom = bottom;
  }

  // ---- quick-look modal -------------------------------------------
  // #68 finding 9: aria-expanded on a multi tile's toggle is only otherwise
  // synced inside the tiles.forEach of the main render() loop — but
  // openQuick()/closeQuick() don't call the full render() (renderQuick()
  // only touches the modal itself), so without this the attribute would
  // never actually flip at the moment the dialog opens/closes. Looked up by
  // data-id rather than cached, since the grid can be rebuilt (filtering).
  function tileToggleFor(pid) {
    var tile = gridEl.querySelector('[data-lt-tile][data-id="' + pid + '"]');
    return tile ? tile.querySelector('[data-lt-toggle]') : null;
  }

  function openQuick(pid) {
    // #68 finding 1 (P0): capture focus BEFORE the dialog takes over — the
    // synchronous ordering the drawer uses (lt-pack-cart.js openDrawer).
    state.quickLastFocus = document.activeElement;
    state.quickPid = pid;
    renderQuick();
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
    var openToggle = tileToggleFor(pid);
    if (openToggle && openToggle.hasAttribute('aria-haspopup')) openToggle.setAttribute('aria-expanded', 'true');
    // Move focus into the dialog's first real control (rows/material tabs if
    // present, else the Add/Remove button — never assume the DOM order, the
    // same defensive query the drawer's Tab-trap uses).
    var items = focusableIn(mCard);
    if (items.length) items[0].focus();
  }
  function closeQuick() {
    var closingToggle = tileToggleFor(state.quickPid);
    state.quickPid = null; modal.hidden = true; document.body.style.overflow = '';
    if (closingToggle && closingToggle.hasAttribute('aria-haspopup')) closingToggle.setAttribute('aria-expanded', 'false');
    // #68 finding 1: return focus to whatever opened the dialog (tile toggle,
    // zoom button, or a deep-link's synthetic open) — synchronous, like the
    // drawer's closeDrawer().
    if (state.quickLastFocus && state.quickLastFocus.focus) state.quickLastFocus.focus({ preventScroll: true });
    state.quickLastFocus = null;
    // #46 polish: leave the deep-linked tile in view (one-shot; skip if a
    // re-render detached the stored element).
    if (deepLinkTile) {
      if (deepLinkTile.isConnected !== false && deepLinkTile.scrollIntoView) deepLinkTile.scrollIntoView({ block: 'center' });
      deepLinkTile = null;
    }
  }

  function renderQuick() {
    var p = byId[state.quickPid];
    if (!p) return;

    // #34-followup: 1. CATEGORY KICKER (motif category, HIDDEN when none resolves).
    var motif = resolveMotif(p);
    if (mKicker) {
      if (motif) { mKicker.textContent = motif.toUpperCase(); mKicker.hidden = false; }
      else { mKicker.textContent = ''; mKicker.hidden = true; }
    }

    // 2. NAME.
    mName.textContent = p.title;

    // 3. TYPE · PRICE. Multi ranges: "Sticker sheet · €2.79 each" ("each" only when
    // the single themes share a price — priced off the singles, same rule as the
    // tile). Singles: "{type} · €{price}". Both use the human type name.
    if (mMeta) mMeta.textContent = itemTypeDisplay(p) + ' · ' + quickPriceLabel(p);

    // 4. RANGE LINE (multi only): one line on the mechanic, adapted to our rules
    // (NO "add both" wording). Uses the real theme count (single designs). Singles
    // get NO blurb (#28 killed per-product blurbs as noise — that still stands).
    if (mRange) {
      if (p.multi) {
        var themeCount = p.singles.length || p.designs.length;
        var noun = rangeNoun(p, themeCount);   // "themes" for a Theme axis, else "designs"
        mRange.textContent = LT('range_line', '{n} hand-drawn {noun} in this range. Each counts toward your discount.')
          .replace('{n}', themeCount).replace('{noun}', noun);  // #47 em-dash sweep
        mRange.hidden = false;
      } else {
        mRange.textContent = '';
        mRange.hidden = true;
      }
    }

    // image: product shot, or the variant shot of the last selected design
    var img = p.image;
    p.designs.forEach(function (d) {
      var key = findSelectedKey(p, d);
      if (key) {
        Object.keys(d.byMat).forEach(function (m) { if (d.byMat[m].id === key && d.byMat[m].image) img = d.byMat[m].image; });
      }
    });
    // #26: give the modal image a srcset + sizes like the tiles have. sizes
    // matches the #30 media column (570px desktop; full width mobile) so the
    // browser picks a candidate at least as wide as the crop window — an
    // undersized pick would be magnified by object-fit:cover. #28: request the
    // image UNCROPPED (fullImgUrl = width only, crop suffix/params stripped) so
    // the cover crop works from the whole photo, not a baked-in square crop.
    // The <img> carries no width/height attributes on purpose: the frame's CSS
    // (aspect-ratio box + width/height:100%) owns the geometry. This same block
    // also runs for the variant-image swap below (img is reassigned to the
    // selected design's featured_image), so the swap gets the same treatment.
    if (img) {
      mImg.src = fullImgUrl(img, 760);
      mImg.srcset = [380, 560, 760, 1120].map(function (w) { return fullImgUrl(img, w) + ' ' + w + 'w'; }).join(', ');
      mImg.sizes = '(max-width: 749px) 100vw, 570px';
      mImg.hidden = false;
      mImg.alt = p.title;
    } else {
      mImg.hidden = true;
    }

    // material segment
    // #68 P0: same wipe-and-rebuild-drops-focus bug as the variant rows and
    // renderMatSwatches — setMaterial() -> render() runs synchronously.
    var mMatsFocusIdx = mMats.contains(document.activeElement)
      ? Array.prototype.indexOf.call(mMats.children, document.activeElement) : -1;
    mMats.innerHTML = '';
    mMats.hidden = p.materials.length < 2;
    if (p.materials.length > 1) {
      p.materials.forEach(function (m, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'lt-bap__mat' + (i === p.matIdx ? ' is-active' : '');
        b.textContent = m;
        var anyAvail = p.designs.some(function (d) { var c = d.byMat[m]; return c && c.available; });
        if (!anyAvail) { b.disabled = true; b.title = LT('sold_out', 'Sold out'); }
        b.addEventListener('click', function () { setMaterial(p, i); });
        mMats.appendChild(b);
      });
    }
    if (mMatsFocusIdx > -1 && mMats.children[mMatsFocusIdx]) mMats.children[mMatsFocusIdx].focus();

    var anyOn = selectedCountFor(p) > 0;

    if (p.multi) {
      mToggle.hidden = true;
      mPicker.hidden = false;
      if (mQty) mQty.hidden = true;   // #34: single-design stepper is not used on multi
      // #34-followup: no add-all button (Nick 2026-07-17). Rows are MULTI-select;
      // once a row is on, its price swaps for a per-row [− N +] stepper. The row is
      // a <div> (not a <button>) so it can legally hold the stepper's buttons; the
      // whole row is clickable to ADD when off (data-lt-vrow-add), and the stepper
      // buttons (data-lt-vrow-dec / -inc) stopPropagation so they never re-trigger
      // the add. − at qty 1 removes the design (toggleDesign off).
      // #68 P0: every mutation (toggleDesign/incQty/decQty) runs render() ->
      // renderQuick() synchronously, which wipes and rebuilds mVariants from
      // scratch. If the keyboard focus was on a row/button in here, the
      // browser drops it to <body> the instant that element is removed —
      // a keyboard user who just pressed Enter to add a design would be
      // silently ejected from the dialog entirely. Capture WHERE focus was
      // (row index + which control) before the wipe, restore the equivalent
      // control on the freshly-built row after.
      var vrowFocus = null;
      if (mVariants.contains(document.activeElement)) {
        var activeRow = document.activeElement.closest('.lt-bap__vrow');
        if (activeRow) {
          var idx = Array.prototype.indexOf.call(mVariants.children, activeRow);
          var role = document.activeElement === activeRow ? 'row'
            : document.activeElement.hasAttribute('data-lt-vrow-check') ? 'check'
            : document.activeElement.hasAttribute('data-lt-vrow-dec') ? 'dec'
            : document.activeElement.hasAttribute('data-lt-vrow-inc') ? 'inc'
            : null;
          if (idx > -1 && role) vrowFocus = { idx: idx, role: role };
        }
      }
      mVariants.innerHTML = '';
      p.designs.forEach(function (d) {
        var cell = cellFor(p, d);
        var onKey = findSelectedKey(p, d);
        var on = !!onKey;
        var entry = on ? state.selected[onKey] : null;
        var qty = entry ? (entry.qty || 1) : 0;
        var row = document.createElement('div');
        row.className = 'lt-bap__vrow' + (on ? ' is-on' : '');
        var avail = cell && cell.available;
        if (!avail) { row.classList.add('lt-bap__vrow--sold'); }
        var units = cell ? unitsForVariant(cell.id, d.value) : 1;
        // price cell (shown when OFF) and stepper cell (shown when ON) both live in
        // the row; CSS + [hidden] swap them so the 56px min-height never shifts.
        var priceHtml = '<span class="lt-bap__vprice"' + (on ? ' hidden' : '') + '>' +
            (avail ? fmt(cell.price) : LT('sold_out', 'Sold out')) + '</span>';
        var stepHtml = '<div class="lt-bap__vstepper"' + (on ? '' : ' hidden') + '>' +
            '<button type="button" class="lt-bap__vstepbtn" data-lt-vrow-dec aria-label="' + LT('fewer', 'Fewer') + '">−</button>' +
            '<span class="lt-bap__vstepqty" data-lt-vrow-qty>' + (qty || 1) + '</span>' +
            '<button type="button" class="lt-bap__vstepbtn" data-lt-vrow-inc aria-label="' + LT('more', 'More') + '">+</button>' +
          '</div>';
        // check bubble content: ✓ at qty 1, the number at qty>1 (per-theme count),
        // empty when off — set here (not via CSS ::after) so multi-select rows can
        // show their own quantity.
        var checkTxt = on ? (qty > 1 ? String(qty) : '✓') : '';
        // i46 item 3 (2026-07-20): the checkmark itself is now also a
        // deselect control (data-lt-vrow-check) when the row is ON — before
        // this, − was the ONLY way off a row, so a qty>1 selection needed N
        // presses to clear. Only rendered clickable-looking when on (CSS
        // below); when off it's still just the empty bubble the row-click
        // add path already covers.
        var checkHtml = '<span class="lt-bap__vcheck"' +
            (on ? ' data-lt-vrow-check role="button" tabindex="0" aria-label="' + LT('remove_of_pack_full', 'Remove {name} from pack').replace('{name}', escapeHtml(d.label)) + '"' : '') +
          '>' + checkTxt + '</span>';
        row.innerHTML =
          checkHtml +
          '<span class="lt-bap__vname">' + escapeHtml(d.label) +
            (units > 1 ? ' <span class="lt-bap__vunits">' + LT('counts_as', 'counts as {n}').replace('{n}', units) + '</span>' : '') +
          '</span>' +
          priceHtml + stepHtml;
        // ADD when off: click anywhere on the row. When on, the row body is inert
        // (the stepper owns qty); guard against re-adding by checking on-state.
        if (avail) {
          row.setAttribute('data-lt-vrow-add', '');
          row.addEventListener('click', function () {
            if (!findSelectedKey(p, d)) toggleDesign(p, d);   // add only when off
          });
          // #68 P0 (finding 1's actual root cause): this row is a <div> with a
          // click listener and NOTHING else — no tabindex, no keydown, no role.
          // A keyboard user could never reach it, which is exactly why "build a
          // pack with a multi-design product" was impossible without a mouse.
          // Only wired for the OFF state — once on, the row body goes back to
          // being inert (matches the click guard above) and the check bubble +
          // stepper buttons (already real, already keyboard-operable) take over.
          if (!on) {
            row.setAttribute('role', 'button');
            row.setAttribute('tabindex', '0');
            row.setAttribute('aria-label', LT('add_to_pack_aria_full', 'Add {name} to pack').replace('{name}', d.label));
            row.addEventListener('keydown', function (e) {
              if (e.key !== 'Enter' && e.key !== ' ') return;
              e.preventDefault();
              if (!findSelectedKey(p, d)) toggleDesign(p, d);
            });
          }
        }
        var decBtn = row.querySelector('[data-lt-vrow-dec]');
        var incBtn = row.querySelector('[data-lt-vrow-inc]');
        var checkEl = row.querySelector('[data-lt-vrow-check]');
        if (incBtn) incBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          var k = findSelectedKey(p, d);
          if (k) incQty(k);
        });
        if (decBtn) decBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          var k = findSelectedKey(p, d);
          if (!k) return;
          if ((state.selected[k].qty || 1) > 1) decQty(k);   // qty>1: step down
          else toggleDesign(p, d);                            // − at 1: remove
        });
        // i46 item 3: checkmark toggles the whole row off regardless of qty
        // (toggleDesign deletes the selection outright — unlike decBtn, which
        // only steps qty down by one). stopPropagation so the bubble to the
        // row-level add-when-off listener above never re-adds it in the same
        // click; Enter/Space fire it too since the bubble is a role="button".
        if (checkEl) {
          checkEl.addEventListener('click', function (e) {
            e.stopPropagation();
            toggleDesign(p, d);
          });
          checkEl.addEventListener('keydown', function (e) {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            e.stopPropagation();
            toggleDesign(p, d);
          });
        }
        mVariants.appendChild(row);
      });
      // #68 P0: restore focus captured above. The row that had focus may no
      // longer offer the SAME control (e.g. the check bubble only exists once
      // a row is on) — fall back to whatever that row now offers, in the
      // order a keyboard user would expect to land: check bubble, then the
      // row body itself (off rows), then the row as a last resort.
      if (vrowFocus) {
        var restoreRow = mVariants.children[vrowFocus.idx];
        if (restoreRow) {
          var restoreTarget =
            (vrowFocus.role === 'check' && restoreRow.querySelector('[data-lt-vrow-check]')) ||
            (vrowFocus.role === 'dec' && restoreRow.querySelector('[data-lt-vrow-dec]')) ||
            (vrowFocus.role === 'inc' && restoreRow.querySelector('[data-lt-vrow-inc]')) ||
            restoreRow.querySelector('[data-lt-vrow-check]') ||
            (restoreRow.hasAttribute('tabindex') ? restoreRow : null) ||
            restoreRow.querySelector('[data-lt-vrow-dec]');
          if (restoreTarget && restoreTarget.focus) restoreTarget.focus();
        }
      }
    } else {
      mPicker.hidden = true;
      // #27: the picker's [hidden] alone does NOT hide its rows — .lt-bap__variants
      // is display:flex, which beats the [hidden] attribute's default display:none.
      // So the previous multi-option item's rows stayed visible (but unclickable)
      // when re-opening a no-option item. Clear them out on every no-option render.
      mVariants.innerHTML = '';
      mToggle.hidden = false;
      var d0 = p.designs[0];
      var cell0 = cellFor(p, d0);
      var on0 = !!findSelectedKey(p, d0);
      mToggle.disabled = !on0 && (!cell0 || !cell0.available);
      mToggle.textContent = mToggle.disabled ? LT('sold_out', 'Sold out') : (on0 ? LT('remove_from_pack', 'Remove from pack') : LT('add_to_pack', 'Add to pack'));
      mToggle.classList.toggle('is-in', on0);
      // #34: quantity stepper appears once the design is in the pack. Its value
      // is the selected variant's qty; − is disabled at 1 (removal goes through
      // the "Remove from pack" toggle, matching the prototype's decColor gate).
      if (mQty) {
        var qKey = on0 ? findSelectedKey(p, d0) : null;
        var qEntry = qKey ? state.selected[qKey] : null;
        mQty.hidden = !qEntry;
        if (qEntry) {
          mQtyVal.textContent = String(qEntry.qty || 1);
          mQtyDec.disabled = (qEntry.qty || 1) <= 1;
        }
      }
    }

    mDone.hidden = !anyOn;
  }

  // ---- edit mode (#34, contract C5) -------------------------------
  // Load the pack named by ?edit=<token> from the cart into the builder state.
  // Each item line rebuilds a state.selected entry keyed on the cart variant id
  // with its qty. Because rows are multi-select, one product may legitimately
  // restore SEVERAL designs (each its own variant id) — this loop handles that
  // for free by keying per variant. The journal line (role 'journal') flips
  // journalAdded and selects its material/colour.
  // On success the CTA becomes "Update pack" and points at replacePack().
  function preloadPack(token) {
    if (!window.LtPackCart || typeof window.LtPackCart.getPacks !== 'function') {
      // Helper not present — cannot preload. Stay in plain "Add pack" mode and
      // say nothing louder than a console note; the page still works as a builder.
      state.editToken = null;
      return;
    }
    window.LtPackCart.getPacks().then(function (packs) {
      var pack = (packs || []).filter(function (pk) { return pk.token === token; })[0];
      if (!pack) { state.editToken = null; render(); return; }
      state.selected = {};
      (pack.items || []).forEach(function (it) {
        var role = (it.properties || {})[ROLE_PROP];
        if (role === 'journal') {
          // Re-select the journal variant: find its material/colour index.
          state.journalAdded = true;
          selectJournalVariant(it.variantId);
          return;
        }
        var ref = byVariant[it.variantId];
        if (!ref) return;                       // variant no longer in catalog — skip
        var c = null;
        Object.keys(ref.d.byMat).forEach(function (m) { if (ref.d.byMat[m].id === it.variantId) c = ref.d.byMat[m]; });
        if (!c) return;
        var entry = selectionEntry(ref.p, ref.d, c);
        entry.qty = Math.max(1, it.quantity || 1);
        state.selected[it.variantId] = entry;
      });
      render();
    }).catch(function () {
      // Fetch failed — drop edit mode, leave the builder empty and usable.
      state.editToken = null;
      render();
    });
  }

  // i45 item 2 (2026-07-20): ?product=<handle> from the PDP "Add to a pack"
  // CTA — scroll to that design's tile and open its quick-look, once, right
  // after the catalog loads. Fallback per spec: a handle that isn't in the
  // builder catalog (journal, or any product outside the 3 builder
  // collections) is a SILENT no-op — plain builder, no error, no console
  // noise. Consumes state.productParam unconditionally so a later render()
  // pass (e.g. a retry after a failed fetch) never re-triggers the jump.
  function preselectProduct() {
    if (!state.productParam) return;
    var handle = state.productParam;
    state.productParam = null;
    var target = null;
    for (var i = 0; i < products.length; i++) {
      if (products[i].handle === handle) { target = products[i]; break; }
    }
    if (!target) return;   // not in catalog — normal builder, nothing shown
    var idx = products.indexOf(target);
    var tile = tiles[idx];
    if (tile && tile.scrollIntoView) tile.scrollIntoView({ block: 'center' });
    // #46 polish (Vera 2026-07-20 caveat): this pre-open scroll lands wrong by
    // the time the customer closes the modal — the tiles above the target are
    // content-visibility:auto, so their heights settle only as images load,
    // shifting the target while the modal hides the drift. Remember the tile
    // and re-scroll once at close, when layout is stable.
    deepLinkTile = tile || null;
    openQuick(target.id);
  }

  // ---- preset pack links (#146, spec #145) ---------------------------------
  // A preset link opens the builder already filled with an example pack from
  // a mail or post:  /pages/build-a-pack?pack=<entry>,<entry>,...
  // where an entry is a product handle, optionally ":Design" for products
  // with several designs (e.g. "warm-winter:Warm Winter mini").
  //
  // resolvePresetPack turns that list into a concrete pack. It is the ONE
  // place the rules live: the builder (#148) and the campaign link checker
  // (tools/pack-link-check.mjs) both run this exact function, so "the checker
  // says OK" means "the shopper gets that pack". For that reason it is pure:
  // no page, no network, no builder state; everything comes in as arguments,
  // which is also what lets the checker cut it out of this file and run it.
  //
  // In:
  //   rawParam  the pack value as it appears in the address bar (still
  //             URL-encoded is fine, already-decoded is fine too)
  //   catalog   the builder's product list, as normalizeProduct() builds it
  //             (the `products` array)
  // Out: { picks, skips, rows }
  //   picks  what goes in the pack, one per distinct variant:
  //          { pid, handle, title, designKey, designLabel, isBundle,
  //            material, variantId, qty }
  //          A repeated entry raises qty. designKey is the design's option
  //          value (d.value), so #148 can find the design object again.
  //   skips  entries that could not be added: { entry, reason } with reason
  //          one of not-in-builder | sold-out | needs-design |
  //          unknown-design | over-limit. For needs-design / unknown-design
  //          it also carries `options` (the product's design values) so a
  //          link author sees what to write.
  //   rows   one per entry in link order (ok or skipped), for the checker's
  //          table: { entry, handle, design, status: 'ok' | reason, ... }
  //
  // Rules (spec #145, "Resolution rules"), in the order they are applied:
  //   1. more than 30 entries: the rest is over-limit (keeps the page fast)
  //   2. handle not in the builder catalog            -> not-in-builder
  //   3. every design of the product sold out         -> sold-out
  //   4. no ":Design" and the product has >1 design   -> needs-design
  //      (we never guess which theme the mail meant)
  //   5. ":Design" that matches no design             -> unknown-design
  //   6. material: the builder's own default (p.matIdx, set by
  //      normalizeProduct: first material with any available cell). If THIS
  //      design is sold out in that material, the first material where it is
  //      available; if none                            -> sold-out
  //      (With today's default rule this always lands on "the first material
  //      in which this design is available". We still start from p.matIdx so
  //      a future change to the builder's default carries over by itself.
  //      p.matIdx is only changed by setMaterial(), a shopper click, so it is
  //      still the untouched default at first render, where #148 applies it.)
  // Matching ignores case, surrounding spaces and runs of spaces. A "+" is
  // treated as a space on both sides, because mail tools write spaces as "+"
  // while some real design values contain one ("Coffee + Bakery"); comparing
  // them the same way keeps both working.
  // Never throws: an empty or garbled value simply gives an empty result.
  function resolvePresetPack(rawParam, catalog) {
    var MAX_ENTRIES = 30;
    var out = { picks: [], skips: [], rows: [] };
    if (typeof rawParam !== 'string' || !rawParam) return out;
    var list = Object.prototype.toString.call(catalog) === '[object Array]' ? catalog : [];

    function norm(s) { return String(s).replace(/\+/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase(); }
    // Percent-decoding that cannot throw: a stray "%" (or a broken sequence
    // from a mail client) leaves just that bit as it was.
    function decode(s) {
      try { return decodeURIComponent(s); } catch (e) {
        return s.replace(/%[0-9a-f]{2}/gi, function (m) {
          try { return decodeURIComponent(m); } catch (e2) { return m; }
        });
      }
    }
    function skip(row, reason, options) {
      row.status = reason;
      var s = { entry: row.entry, reason: reason };
      if (options) { s.options = options; row.options = options; }
      out.skips.push(s);
    }

    var byHandle = {};
    list.forEach(function (p) { if (p && p.handle && !byHandle[norm(p.handle)]) byHandle[norm(p.handle)] = p; });
    var pickAt = {};   // variantId -> index in out.picks (repeats raise qty)
    var counted = 0;

    decode(rawParam).split(',').forEach(function (part) {
      var text = part.trim();
      if (!text) return;                                   // "a,,b" / trailing comma
      var colon = text.indexOf(':');
      var handle = norm(colon === -1 ? text : text.slice(0, colon));
      var design = colon === -1 ? '' : norm(text.slice(colon + 1));
      if (!handle) return;                                 // ":" or ":Design" names no product
      counted++;
      var row = { entry: text, handle: handle, design: design, status: 'ok' };
      out.rows.push(row);
      if (counted > MAX_ENTRIES) return skip(row, 'over-limit');

      var p = byHandle[handle];
      if (!p) return skip(row, 'not-in-builder');
      row.pid = p.id;
      row.title = p.title;
      if (p.soldout) { row.available = false; return skip(row, 'sold-out'); }

      var values = p.designs.map(function (x) { return x.value; });
      var d = null;
      if (!design) {
        if (p.designs.length !== 1) return skip(row, 'needs-design', values);
        d = p.designs[0];
      } else {
        for (var i = 0; i < p.designs.length; i++) { if (norm(p.designs[i].value) === design) { d = p.designs[i]; break; } }
        if (!d) return skip(row, 'unknown-design', values);
      }

      var mat = p.materials[p.matIdx];
      var cell = d.byMat[mat];
      if (!cell || !cell.available) {
        cell = null;
        for (var j = 0; j < p.materials.length; j++) {
          var c = d.byMat[p.materials[j]];
          if (c && c.available) { cell = c; mat = p.materials[j]; break; }
        }
      }
      if (!cell) { row.available = false; return skip(row, 'sold-out'); }

      row.designKey = d.value;
      row.isBundle = !!d.isBundle;
      row.material = mat;
      row.variantId = cell.id;
      row.available = true;
      var key = String(cell.id);
      if (pickAt[key] !== undefined) { out.picks[pickAt[key]].qty++; return; }
      pickAt[key] = out.picks.length;
      out.picks.push({
        pid: p.id, handle: p.handle, title: p.title,
        designKey: d.value, designLabel: d.label, isBundle: !!d.isBundle,
        material: mat, variantId: cell.id, qty: 1
      });
    });
    return out;
  }

  // ---- applying a preset pack (#148, spec #145 "Applying") -----------------
  // applyPresetPack takes the builder state and what resolvePresetPack made of
  // the link, and returns the NEW state the builder should show. Pure: it
  // never changes the state it is given and touches no page or storage, so
  // the node test can cut it out of this file and run it
  // (_previews/preset-pack-apply.logic.test.mjs).
  //
  // In:
  //   current    the builder state (uses .selected and .editToken)
  //   resolved   resolvePresetPack(...)'s { picks, skips, rows }
  //   catalog    the builder's product list (the `products` array)
  //   makeEntry  how the builder turns (product, design, variant cell) into
  //              a selection entry; the builder passes selectionEntry, so a
  //              preset pick looks exactly like a hand-picked one (same
  //              label, price, "counts as N" units)
  // Out: a copy of `current` with `selected` replaced by the pack.
  function applyPresetPack(current, resolved, catalog, makeEntry) {
    // ?edit= wins outright: the shopper is editing a pack that is already in
    // the cart, and a preset link must never overwrite that.
    if (current.editToken) return current;
    // A link that names no entry at all ("?pack=" / ",,,") is not a pack:
    // keep the normal builder, including a stored draft (spec M20). A link
    // whose entries were ALL skipped does replace the draft (empty builder,
    // spec M22), because the shopper did ask for that pack.
    if (!resolved || !resolved.rows || !resolved.rows.length) return current;
    var byPid = {};
    (catalog || []).forEach(function (p) { if (p) byPid[p.id] = p; });
    var selected = {};
    (resolved.picks || []).forEach(function (pick) {
      var p = byPid[pick.pid];
      if (!p) return;
      var d = null, c = null;
      p.designs.forEach(function (x) { if (x.value === pick.designKey) d = x; });
      if (!d) return;
      Object.keys(d.byMat).forEach(function (m) { if (String(d.byMat[m].id) === String(pick.variantId)) c = d.byMat[m]; });
      if (!c) return;
      var entry = makeEntry(p, d, c);
      // The resolver already merged repeats of one entry into pick.qty
      // ("x,x" = two of x), so a repeat shows as one row with a stepper at 2.
      entry.qty = Math.max(1, pick.qty || 1);
      selected[c.id] = entry;
    });
    var next = {};
    Object.keys(current).forEach(function (k) { next[k] = current[k]; });
    next.selected = selected;
    return next;
  }

  // ---- the skipped-designs notice (#149, spec #145 "Notice") ---------------
  // presetNotice decides WHAT the shopper is told after a preset link was
  // applied; renderPresetNotice (next to openTray/closeTray) only puts it on
  // screen. Pure,
  // like the two functions above, so the node test can cut it out of this
  // file and run it (_previews/preset-pack-notice.logic.test.mjs).
  //
  // In:  resolvePresetPack(...)'s { picks, skips, rows }, or null when the
  //      page was opened without a pack link (state.presetResult).
  // Out: null                          nothing to say: no link, a link that
  //                                    names no entry ("?pack=,,,"), or every
  //                                    entry made it into the pack
  //      { variant: 'some', count: N } N link entries could not be added
  //      { variant: 'all' }            the link named entries, none resolved
  //
  // Why count = number of skipped ENTRIES (Timothy's call, 2026-09-25): every
  // entry in the link that did not make it counts, "over-limit" included,
  // and a skipped bundle counts as 1 (Kindle: the shopper thinks in designs
  // they saw in the mail, not in units). The reasons themselves (sold out,
  // not in the builder, ...) are for the link checker only; shoppers never
  // see them, so this function never looks at them.
  function presetNotice(presetResult) {
    if (!presetResult || !presetResult.rows || !presetResult.rows.length) return null;
    var skipped = (presetResult.skips || []).length;
    if (!skipped) return null;
    if (!(presetResult.picks || []).length) return { variant: 'all' };
    return { variant: 'some', count: skipped };
  }

  // Point jState at the material/colour that owns this journal variant id.
  function selectJournalVariant(variantId) {
    if (!jMaterials.length) return;
    for (var mi = 0; mi < jMaterials.length; mi++) {
      var cols = jMaterials[mi].colors;
      for (var ci = 0; ci < cols.length; ci++) {
        if (cols[ci].variantId === variantId) { jState.material = mi; jState.color = ci; return; }
      }
    }
  }

  // ---- draft persistence (#52) -------------------------------------
  // The builder lost all progress on refresh — persist the in-progress pack to
  // sessionStorage and restore it once, right after the catalog loads. All
  // storage access is wrapped in try/catch: Safari private mode throws on
  // getItem/setItem/removeItem, and a storage failure must never break the
  // builder itself (worst case: the draft feature quietly does nothing).
  // #69 P2-4: render() is called on EVERY filter/search re-render too, but
  // only a selection or journal-state change actually needs persisting.
  // Rather than hunting down and re-wiring every selection-mutating call
  // site (toggleDesign, qty steppers, journal toggle, mystery add/remove —
  // easy to miss one and silently break persistence), saveDraft() keeps
  // being called from render() every time but skips the actual
  // JSON.stringify + sessionStorage.setItem work when the payload is
  // byte-identical to the last save. Same outcome the worklist asks for
  // (no write on a search/filter render), zero risk of a missed call site.
  var lastDraftSig = null;

  function saveDraft() {
    if (state.editToken) return;   // #52: an edit-in-progress isn't a "new pack" draft — never persist it under the plain draft key (see the ?edit= clear above)
    if (!draftReady) return;       // #52: guards the pre-catalog render() calls from clobbering a stored draft before restoreDraft() gets a chance to read it
    try {
      var items = [];
      Object.keys(state.selected).forEach(function (k) {
        items.push({ variantId: k, qty: state.selected[k].qty || 1 });
      });
      var payload = {
        items: items,
        // #50: journalDismissed rides in the SAME #52 draft store (no new store),
        // so the nudge opt-out survives a refresh/tier change for the session.
        journal: { added: state.journalAdded, kit: state.journalKit, dismissed: state.journalDismissed, material: jState.material, color: jState.color }
      };
      var sig = JSON.stringify(payload);
      if (sig === lastDraftSig) return;   // #69: nothing selection/journal-related changed since the last save
      lastDraftSig = sig;
      sessionStorage.setItem(DRAFT_KEY, sig);
    } catch (e) { /* #52: storage unavailable — draft persistence is best-effort only */ }
  }

  function clearDraft() {
    lastDraftSig = null;   // #69: force the next real change to persist, even if it matches what was just cleared
    try { sessionStorage.removeItem(DRAFT_KEY); } catch (e) { /* nothing to clear / storage unavailable */ }
  }

  // #52: restore a stored draft once, right after the catalog loads. Mirrors
  // preloadPack()'s validation below: every variantId is looked up in
  // byVariant (just built from the fresh catalog fetch) and anything unknown
  // is silently dropped — the catalog may have changed (product removed or
  // renamed) since the draft was saved. Selections are rebuilt through
  // selectionEntry(), the same entry-construction path every other selection
  // goes through, rather than hand-rolling a parallel entry shape.
  //
  // Unlike preloadPack (which restores a cart line even if it has since sold
  // out, because that line is already committed in the cart), a draft variant
  // that has since sold out is dropped too: a draft was never committed to
  // anything, so there is nothing to honor by keeping it.
  function restoreDraft() {
    var raw;
    try { raw = sessionStorage.getItem(DRAFT_KEY); } catch (e) { return; }
    if (!raw) return;
    var draft;
    try { draft = JSON.parse(raw); } catch (e) { return; }
    if (!draft) return;

    (draft.items || []).forEach(function (it) {
      var ref = byVariant[it.variantId];
      if (!ref) return;   // #52: product no longer in the builder catalog — drop silently
      var c = null;
      Object.keys(ref.d.byMat).forEach(function (m) {
        if (String(ref.d.byMat[m].id) === String(it.variantId)) c = ref.d.byMat[m];
      });
      if (!c || !c.available) return;   // #52: variant sold out / removed since the draft was saved
      var entry = selectionEntry(ref.p, ref.d, c);
      entry.qty = Math.max(1, it.qty || 1);
      state.selected[c.id] = entry;
    });

    // #50: the nudge opt-out restores independently of the added state — a
    // session that dismissed the nudge (without adding) must stay dismissed.
    if (draft.journal && draft.journal.dismissed) state.journalDismissed = true;

    if (draft.journal && draft.journal.added && journalData && jMaterials.length) {
      var mi = draft.journal.material;
      if (typeof mi === 'number' && jMaterials[mi]) {
        var ci = draft.journal.color;
        if (typeof ci !== 'number' || !jMaterials[mi].colors[ci]) ci = 0;
        jState.material = mi;
        jState.color = ci;
        state.journalAdded = true;
        state.journalKit = !!draft.journal.kit;
      }
      // #52: an out-of-range material index means the journal's variant layout
      // changed since the draft was saved — drop the journal line silently
      // rather than guess at a replacement.
    }
  }

  // ---- add to cart ------------------------------------------------
  function addPackToCart() {
    var keys = Object.keys(state.selected);
    if (!keys.length) return;
    // #34 edit mode: reuse the SAME token so replacePack tears down and rebuilds
    // exactly this pack; otherwise mint a fresh token for a brand-new pack.
    var editing = !!state.editToken;
    var token = editing ? state.editToken : 'pack-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 6);
    var items = keys.map(function (k) {
      var props = {}; props[PACK_PROP] = token; props[ROLE_PROP] = 'item';
      // #34 (C1): send the REAL per-design quantity. The Cart Transform merges
      // these item lines into one "Custom Pack" parent and the discount function
      // reads units*qty off it; a qty of 2 on a "counts as 2" bundle is 4 items.
      return { id: parseInt(k, 10), quantity: state.selected[k].qty || 1, properties: props };
    });
    if (state.journalAdded) {
      var vId = currentVariantId();
      if (vId) { var jp = {}; jp[PACK_PROP] = token; jp[ROLE_PROP] = 'journal'; items.push({ id: vId, quantity: 1, properties: jp }); }
    }

    addPackBtn.disabled = true;
    var original = addPackBtn.textContent;
    addPackBtn.textContent = editing ? LT('updating_ellipsis', 'Updating…') : LT('adding_ellipsis', 'Adding…');
    // Edit mode goes through the shared helper (teardown old lines + re-add with
    // the same token); a new pack posts straight to /cart/add.js. Both resolve to
    // the same drawer-open sequence below.
    var send = editing && window.LtPackCart && typeof window.LtPackCart.replacePack === 'function'
      ? window.LtPackCart.replacePack(token, items)
      : fetch(CART_ADD, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: items })
        }).then(function (r) { if (!r.ok) throw new Error('cart'); return r.json(); });
    send.then(function () {
      clearDraft();   // #52: the pack made it to the cart (new pack or edit/replace) — the draft's job is done
      // #17: open the cart DRAWER instead of navigating to /cart (drawer
      // everywhere, site-wide consistency). Regular product adds already open
      // the drawer: on add success the theme fires `theme:cart:popdown`, and
      // custom.js intercepts it to (a) re-fetch the cart, (b) dispatch
      // `theme:cart:change` so the drawer marks itself stale, and (c) dispatch
      // `theme:drawer:open` so it re-fetches its HTML (all fresh pack lines).
      // We reproduce that exact three-step sequence here rather than faking a
      // pop-down event, because the pop-down path needs a variant payload and a
      // pop-down element we don't have after a multi-line pack add. Same
      // mechanism, so the drawer content is fresh with every pack line present.
      var drawer = document.querySelector('[data-drawer="drawer-cart"]');
      if (!drawer) {
        // No drawer on this page (e.g. a template without group-overlay) — keep
        // the old behaviour so a pack can still be reviewed.
        window.location.href = CART_URL;
        return;
      }
      // Reset the button now; the drawer takes over the "done" feedback.
      addPackBtn.disabled = false;
      addPackBtn.textContent = original;
      fetch(CART_URL + '.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (response) { return response.json(); })
        .then(function (cart) {
          document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: cart }, bubbles: true }));
          drawer.dispatchEvent(new CustomEvent('theme:drawer:open', { bubbles: false }));
        })
        .catch(function () {
          // Network hiccup fetching the cart — still open the drawer; it loads
          // its own contents from the api-cart-items section.
          drawer.dispatchEvent(new CustomEvent('theme:drawer:open', { bubbles: false }));
        });
    }).catch(function () {
      addPackBtn.disabled = false;
      addPackBtn.textContent = original;
      alert(editing
        ? LT('catalog_error_updating', 'Sorry, something went wrong updating your pack. Please try again.')
        : LT('catalog_error_adding', 'Sorry, something went wrong adding your pack. Please try again.'));  // #47 em-dash sweep
    });
  }

  // ---- events -----------------------------------------------------
  gridEl.addEventListener('click', function (e) {
    var tile = e.target.closest ? e.target.closest('[data-lt-tile]') : null;
    if (!tile || tile.hasAttribute('data-lt-skel')) return;
    var p = byId[tile.dataset.id];
    if (!p) return;
    // #32: stopPropagation so the zoom click cannot reach any ancestor listener
    // and accidentally toggle selection — critical for single-design tiles whose
    // mediaarea is also the direct-add target. The early return already handles
    // the gridEl listener; stopPropagation defends against any future outer listeners.
    if (e.target.closest('[data-lt-zoom]')) { e.stopPropagation(); openQuick(p.id); return; }
    // #34 hover-stepper on single-design tiles: +/- change the qty of the one
    // selected variant. stopPropagation keeps the click off the tile-toggle.
    if (e.target.closest('[data-lt-tile-inc]') || e.target.closest('[data-lt-tile-dec]')) {
      e.stopPropagation();
      var key = findSelectedKey(p, p.designs[0]);
      if (!key) return;
      if (e.target.closest('[data-lt-tile-inc]')) incQty(key);
      else decQty(key);   // - at 1 removes the entry
      return;
    }
    if (e.target.closest('[data-lt-toggle]')) {
      if (p.soldout) return;
      if (p.multi) { openQuick(p.id); return; }
      // Tap-to-toggle (Nick 2026-07-18): a tap on a selected single tile
      // deselects it ONLY at qty 1. At qty >1 the tap is inert — a stray tap
      // must never wipe a multi-quantity selection; qty moves through the
      // hover stepper / quick-look stepper instead. Same rule on touch, where
      // the stepper pill never shows (no hover) but the quick-look sheet does.
      var selKey = findSelectedKey(p, p.designs[0]);
      if (selKey && (state.selected[selKey].qty || 1) > 1) return;
      toggleDesign(p, p.designs[0]);
    }
  });

  // #69 P2-4: debounce ONLY the search input's render. Every other render()
  // caller (tile clicks, type/motif chip clicks, quick-look open/close, qty
  // steppers) stays perfectly synchronous — #68's focus-restore logic on the
  // quick-look dialogs depends on the click handler's render() finishing
  // before it moves focus, so debouncing those would break Tab-trap/return.
  var searchDebounceTimer = null;
  if (searchEl) searchEl.addEventListener('input', function () {
    state.search = this.value.trim().toLowerCase();
    clearTimeout(searchDebounceTimer);
    searchDebounceTimer = setTimeout(render, 140);
  });
  typeBtns.forEach(function (b) { b.addEventListener('click', function () { state.type = b.dataset.type; render(); }); });

  $$('[data-lt-modal-close]').forEach(function (el) { el.addEventListener('click', closeQuick); });
  mToggle.addEventListener('click', function () {
    var p = byId[state.quickPid];
    if (!p) return;
    toggleDesign(p, p.designs[0]);
  });
  // #30/#34-followup: no add-all button/handler — rows are multi-select and each
  // carries its own add + [− N +] stepper (wired per-row in renderQuick()).
  // #34: single-design quick-look quantity stepper. Operates on the one selected
  // variant of the open product; − at 1 is disabled (render() handles that), so
  // dec here only ever decrements a qty >= 2 and never removes from this control.
  if (mQtyInc) mQtyInc.addEventListener('click', function () {
    var p = byId[state.quickPid];
    if (!p) return;
    var key = findSelectedKey(p, p.designs[0]);
    if (key) incQty(key);
  });
  if (mQtyDec) mQtyDec.addEventListener('click', function () {
    var p = byId[state.quickPid];
    if (!p) return;
    var key = findSelectedKey(p, p.designs[0]);
    if (key && (state.selected[key].qty || 1) > 1) decQty(key);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      if (jmodal && !jmodal.hidden) { closeJournalQuick(); return; }   // #50
      if (giftModal && !giftModal.hidden) { closeGiftModal(); return; }   // #116 round 2
      if (!modal.hidden) closeQuick();
      return;
    }
    // #68 finding 1 (P0): Tab containment while either dialog is open — the
    // drawer's own "rudimentary focus trap" (lt-pack-cart.js), reused as-is.
    if (e.key !== 'Tab') return;
    if (jmodal && !jmodal.hidden) { trapTabIn(jmCard, e); return; }
    if (giftModal && !giftModal.hidden) { trapTabIn(giftCard, e); return; }   // #116 round 2
    if (!modal.hidden) trapTabIn(mCard, e);
  });

  // #116 round 2: gift window wiring. Native <button>s (the
  // photo trigger + the close buttons) get Enter/Space activation
  // for free, same reasoning #68 finding 10 already established for
  // journalOpenEls just below — no manual keydown replica needed.
  giftOpenEls.forEach(function (el) { el.addEventListener('click', openGiftModal); });
  $$('[data-lt-giftmodal-close]').forEach(function (el) { el.addEventListener('click', closeGiftModal); });

  // #50 journal quick-look: open from either photo, close via overlay/Close,
  // add/remove from the modal.
  // #68 finding 10: journalOpenEls are real <button>s now (were
  // div[role=button][tabindex=0]), so Enter/Space activation and the click
  // event are native — the manual keydown replica is gone (it would have
  // double-fired openJournalQuick() per keypress: once from itself, once
  // from the button's own synthesized click). The dismiss-x is a sibling
  // of the button in the markup now, not a descendant, so it can never
  // reach this click handler — no closest()-based exclusion needed either.
  journalOpenEls.forEach(function (el) {
    el.addEventListener('click', openJournalQuick);
  });
  $$('[data-lt-jmodal-close]').forEach(function (el) { el.addEventListener('click', closeJournalQuick); });
  if (jmToggle) jmToggle.addEventListener('click', function () {
    if (state.journalAdded) { state.journalAdded = false; state.journalKit = false; }
    else { state.journalAdded = true; }
    render();
  });
  // #50 opt-out: the mobile round × and the desktop "Not now" both dismiss the
  // nudge for the session. Never touches the added row or the dismissed-vs-added
  // independence; persisted via saveDraft() on the next render().
  dismissJournalEls.forEach(function (el) {
    el.addEventListener('click', function (e) {
      e.stopPropagation();   // the × sits inside the photo's open-quick target
      state.journalDismissed = true;
      render();
    });
  });
  // #50 fix (D1): the restore line clears the opt-out, bringing the nudge (and all
  // add paths) back. Persistence is unchanged — the next saveDraft() records the
  // cleared flag, so a reload no longer traps the customer without an add path.
  $$('[data-lt-restore-journal]').forEach(function (el) {
    el.addEventListener('click', function () { state.journalDismissed = false; render(); });
  });

  // #34 mobile bottom-sheet controls: pill opens, scrim/handle closes, thumbnail
  // tap removes that design. Thumb removal uses delegation on the grid.
  if (pillEl) pillEl.addEventListener('click', openTray);
  // #149: closing the notice (either copy) hides both, for this page view.
  // Only the notice re-renders; the pack itself is not touched.
  presetNoticeEls.forEach(function (el) {
    var closeEl = el.querySelector('[data-lt-pnotice-close]');
    if (closeEl) closeEl.addEventListener('click', function () {
      state.presetNoticeDismissed = true;
      renderPresetNotice();
      focusAfterPresetNotice();
    });
  });
  // #149 follow-up (Timothy's review): the close button that had focus is
  // hidden the moment the notice closes, and the browser then drops focus
  // to <body>: a keyboard or screen-reader user is thrown back to the top
  // of the page. Instead, focus goes to the number the notice explained,
  // the pack count, in whichever form the shopper can actually see:
  //   1. the tray count ("9 items selected"): the desktop bar, or the open
  //      phone sheet. Not while the phone sheet is closed: the tray is
  //      `inert` then (see render()), and inert elements refuse focus.
  //   2. the phone pill (it shows the count and progress) when the sheet
  //      is closed.
  //   3. the catalog count above the grid ("Showing 212 designs") when the
  //      pack is empty on a phone and there is no pill: the notice just said
  //      "This pack isn't available", and the grid is where you pick instead.
  // The two text elements are not focusable by nature; tabindex="-1" lets
  // the script focus them without adding them to the Tab order. The focus
  // ring (keyboard users only) is styled in section-lt-build-a-pack.liquid,
  // next to the notice CSS: the theme's default ring is invisible on the pill.
  function focusAfterPresetNotice() {
    var target = null;
    if (countLabelEl && trayEl && !trayEl.hasAttribute('inert') && countLabelEl.getClientRects().length) target = countLabelEl;
    else if (pillEl && !pillEl.hidden && pillEl.offsetHeight) target = pillEl;
    else if (countEl && countEl.textContent) target = countEl;
    if (!target) return;
    if (target !== pillEl && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus();
  }
  if (scrimEl) scrimEl.addEventListener('click', closeTray);
  trayCloseEls.forEach(function (el) { el.addEventListener('click', closeTray); });
  if (thumbsEl) thumbsEl.addEventListener('click', function (e) {
    var cell = e.target.closest ? e.target.closest('[data-variant]') : null;
    if (!cell) return;
    delete state.selected[cell.dataset.variant];   // tap thumb = remove design
    render();
  });

  // #104 founder round 3, finding 4 (Nick, 2026-08-17): on desktop the nudge's
  // ADD JOURNAL button used to add the default (brown leather) journal
  // instantly, with no way to pick a colour/material before it lands in the
  // pack. Below the Tray A breakpoint the flow is untouched (isDesktopTray()
  // false covers real mobile AND the 750-989px gap that has no Tray A panel
  // to house a "Change" link) — those shoppers still get the instant add and
  // pick material/colour on the row afterward, the same as before this round.
  // At >=990px, route through the SAME quick-look modal the panel's own
  // "Change" link already opens (blocker 1, prior round) — reuses jState /
  // openJournalQuick() / the modal's own Add toggle verbatim, no new state.
  if (addJournalBtn) addJournalBtn.addEventListener('click', function () {
    if (isDesktopTray()) { openJournalQuick(); return; }
    state.journalAdded = true;
    render();
  });
  if (removeJournalBtn) removeJournalBtn.addEventListener('click', function () { state.journalAdded = false; state.journalKit = false; render(); });
  if (addPackBtn) addPackBtn.addEventListener('click', addPackToCart);
  if (retryBtn) retryBtn.addEventListener('click', loadCatalog);

  // #104 (desktop pack-overview panel, own file lt-bap-desktop-tray.js):
  // minimal READ-ONLY seam + the SAME mutation functions the quick-look
  // stepper and mobile thumb-tap already call. Added because Tray A's
  // expanded panel needs per-row [-N+] steppers — a NEW interaction; neither
  // the mobile tray nor the rejected desktop spike had one — and there is no
  // way to reach state.selected / incQty / decQty from outside this closure
  // otherwise. This is the "state seam" the ticket brief anticipated might
  // be needed and asked to be flagged if so.
  //
  // Everything below is ADDITIVE (nothing above this block changed) and
  // read-only for state: every mutation still goes through the exact
  // functions the rest of this module already uses (incQty/decQty/render),
  // so render() stays the single place DOM ever syncs to state — the
  // ticket's "no parallel state" constraint. Rebase note: append-only, at
  // the very end of the file, so it should merge cleanly regardless of where
  // today's #90/#107/#108 commits touched earlier in the file.
  root.ltBapDesktopApi = {
    // One row per selected variant/design — same source (state.selected) the
    // mobile thumb grid already reads, plus the fields Tray A's row needs.
    // w = requested image width in px (caller picks per its own CSS box).
    entries: function (w) {
      w = w || 168;
      return Object.keys(state.selected).map(function (k) {
        var e = state.selected[k];
        var qty = e.qty || 1;
        return {
          key: k,
          label: e.label,
          price: e.price,
          qty: qty,
          lineTotal: fmt(e.price * qty),
          image: e.image ? imgUrl(e.image, w) : ''
        };
      });
    },
    inc: function (key) { incQty(key); },
    dec: function (key) { decQty(key); },
    remove: function (key) { delete state.selected[key]; render(); },
    count: function () { return compute().count; },
    // Journal row summary for the panel (Tray A shows it only while
    // expanded); null when the journal isn't in the pack. Mirrors exactly
    // what the existing jrowEl already displays (same jState/jMaterials) —
    // no second source of truth for material/colour/price.
    //
    // #104 r4 finding 2 (Nick, 2026-08-17): the row used to show the
    // DISCOUNTED price struck against the full price, while a separate green
    // line below claimed only the stationery-tier cut — the journal's own
    // €4.86-vs-€9.86-shaped gap between the two never reconciled ("this is
    // confusing," verbatim). Fix: this row now always shows the PLAIN full
    // price (never struck) — the combined discount line built by tier()
    // below carries the journal's reward too, so [sum of rows] − [green
    // line] = [pack total] with no mental math. journalFull/journalNet stay
    // exposed via compute() itself, not duplicated here.
    journal: function (w) {
      if (!state.journalAdded || !jMaterials.length) return null;
      var c = compute();
      var mat = jMaterials[Math.min(jState.material, jMaterials.length - 1)];
      var col = mat.colors[Math.min(jState.color, mat.colors.length - 1)];
      return {
        title: journalMatWord(mat) + journalColourSuffix(col),
        // #104 r5 (Nick, 2026-08-17, reversing r4's plain-price call): the
        // deal must be visible ON the row — struck full price next to the
        // 10%-capped net (€49.99 → €44.99). To keep the panel reconciling,
        // tier() below carries ONLY the stationery-tier cut again.
        price: fmt(c.journalUnlocked ? c.journalNet : c.journalFull),
        fromPrice: c.journalUnlocked ? fmt(c.journalFull) : null,
        image: col && col.image ? journalImgUrl(col.image, w || 128) : '',
        hex: col ? col.hex : ''   // #104: collapsed-strip journal chip fallback colour when no photo fits at 36px
      };
    },
    openJournalQuick: openJournalQuick,
    // #104 r6 (Nick, 2026-08-19): tier() — the green discount line's data
    // source — is removed with its only consumer (the tray panel line). If a
    // discount summary ever returns, derive it from compute()'s own totals
    // (fullTotal − total), never local re-math: r4's floor-vs-round lesson.
    // Fires after every render() so the panel can re-sync (journal added
    // from the nudge button, grid selection changed while the panel was
    // open, etc.) without polling.
    onRender: function (cb) { if (typeof cb === 'function') desktopTraySubscribers.push(cb); }
  };

  // ---- init -------------------------------------------------------
  render();       // tray/journal state while skeletons show
  loadCatalog();
})();
